import {loadScriptsInOrder} from '@/services/loader'
import {Support} from './viewer/support'
import {SupportOutput} from './viewer/support_output'

const loadStylesheet = (href) => {
  return new Promise((resolve, reject) => {
    const id = href.replace(/\/|\./g, '-')
    if (document.getElementById(id)) {
      return resolve()
    }
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.id = id
    link.href = href
    link.onload = resolve
    document.head.appendChild(link)
  })
}
const loadStylesheetList = (list) => {
  return Promise.all(
    list.map(loadStylesheet)
  )
}

export class InputViewer {
  LIBRARY_DEPS = [
    'https://ajax.googleapis.com/ajax/libs/jquery/1.12.4/jquery.min.js',
    '/static/js/three.js',
    '/static/js/renderers/CanvasRenderer.js',
    '/static/js/controls/TrackballControls.js',
    '/static/js/controls/TransformControls.js',
    '/static/js/Detector.js',
    '/static/js/libs/stats.min.js',
    '/static/js/loaders/STLLoader.js',
    '/static/js/math/Lut.js',
    '/static/js/libs/dat.gui.min.js',
    '/static/js/three-text2d.min.js',
    '/static/js/quaternion.js',
    '/static/js/jquery-ui/jquery-ui.js'
  ]

  STYLESHEET_DEPS = [
    '/static/js/jquery-ui/jquery-ui.css',
    '/static/js/jquery-ui/jquery-tooltip.css'
  ]

  STATIC_PREFIX = '/static/models/stl/binary/'
  PRINTER_CONFIG_LIBRARY = {
    EOSM100: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM100.stl',
      position: [-100, -200, 200],
      zRotation: -15
    },
    EOSM290: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM290.stl',
      position: [-150, -350, 300],
      zRotation: -25
    },
    EOSM400: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM400.stl',
      position: [-300, -500, 400],
      zRotation: -40
    },
    SLM280: {
      base: this.STATIC_PREFIX + 'build_plate_SLM280.stl',
      position: [-250, -350, 300],
      zRotation: -30
    },
    RenishawAM250: {
      base: this.STATIC_PREFIX + 'build_plate_RenishawAM250.stl',
      position: [-250, -350, 300],
      zRotation: -30
    },
    RenishawAM500: {
      base: this.STATIC_PREFIX + 'build_plate_RenishawAM500.stl',
      position: [-250, -350, 350],
      zRotation: -30
    },
    ArcamQ20: {
      base: this.STATIC_PREFIX + 'build_plate_arcamQ20.stl',
      position: [-350, -350, 200],
      zRotation: -15
    }
  }

  constructor (configs, files, callback, finishedLoadingCB, onSave,
    {
      fetchSupport,
      generateSupportExternal,
      getCurvaturePerFaceData,
      supportLoading,
      curvatureLoading,
      contextMenuToggle,
      showHelp,
      changeZRotationAccess,
      savePartsConfig,
      savePlatesConfig,
      openRemovePlateModal
    }) {
    this.PRINTER = configs.config.printer
    this.STL_FILES = files.parts
    this.uploadedSupports = false
    this.isNested = !!configs.nestedConfig
    if ((files.hasOwnProperty('supports') && files.supports.length > 0) || configs.config.isHideSupports) {
      this.STL_SUPPORT_FILES = files.supports || []
      this.uploadedSupports = true
    }
    this.CONFIG = configs.config
    this.old = {
      scale: this.CONFIG.scaling[0],
      supportTransparency: this.uploadedSupports ? 0.7 : 0.2
    }
    if ('nestedConfig' in configs) {
      this.allowZRotation = configs.nestedConfig.allowZRotation
      this.nestedPartsNames = configs.nestedConfig.partsNames
      this.currentPartName = this.nestedPartsNames[0]
      this.old = {
        [this.currentPartName]: {
          scale: this.CONFIG.scaling[0],
          supportTransparency: this.uploadedSupports ? 0.7 : 0.2
        }
      }
      this.oldPosition = []
      this.isManualPlacement = configs.nestedConfig.isManualPlacement
      if (this.isManualPlacement) {
        this.currentPlateName = ''
        this.platesConfig = []
        this.plateMatch = new RegExp(`plate`)
        this.savePlatesConfig = savePlatesConfig
        this.openRemovePlateModal = openRemovePlateModal
        this.currentObject = null
      }
      this.partsConfig = configs.nestedConfig.partsConfig
      this.transformControl = null
      this.marginX = configs.nestedConfig.margin.marginX
      this.marginY = configs.nestedConfig.margin.marginY
    }
    this.isManualPlacementMode = false
    this.PRINTERS_CONFIG = configs.printersConfig
    this.finishedLoadingCB = finishedLoadingCB
    this.STLList = []
    this.finishedLoading = false
    this._saveCallback = onSave
    this.fetchSupport = fetchSupport
    this.generateSupportExternal = generateSupportExternal
    this.getCurvaturePerFaceData = getCurvaturePerFaceData
    this.supportLoading = supportLoading
    this.curvatureLoading = curvatureLoading
    this.contextMenuToggle = contextMenuToggle
    this.changeZRotationAccess = changeZRotationAccess
    this.savePartsConfig = savePartsConfig
    this.showHelp = showHelp
    this.contextMenuIntersect = null

    Promise.all([
      loadStylesheetList(this.STYLESHEET_DEPS),
      loadScriptsInOrder(this.LIBRARY_DEPS)
    ]).then(() => {
      callback && callback()
    })
  }

  rad2deg (rads) {
    return rads * (180.0 / Math.PI)
  }

  deg2rad (degs) {
    return degs * (Math.PI / 180.0)
  }

  unit2mmConstant (unit) {
    var conversionConstant = 1
    switch (unit) {
      case 'm':
        conversionConstant = 1000.0
        break
      case 'cm':
        conversionConstant = 10.0
        break
      case 'inch':
        conversionConstant = 25.4
        break
    }
    return conversionConstant
  }

  preInitializer () {
    this.THREE = window['THREE']
    this.jQuery = window['$']
    this.support = new Support(this.THREE)
    this.dat = window['dat']
    var Detector = window['Detector']
    // Faces select
    this.INTERSECTED = null
    this.restrictedFaces = []
    this.allowedFaces = []
    this.highlightedFaces = []
    this.curvaturePerFacet = []
    this.facetAdjacents = []
    this.maxCurvature = 0

    this.color = {
      restrict: 0xff0000,
      allow: 0x00ff00,
      highlighted: 0xd5d5d5,
      default: 0x93969b,
      erase: 0x93969b
    }

    this.colorObj = {
      restrict: new this.THREE.Color(this.color.restrict),
      allow: new this.THREE.Color(this.color.allow),
      highlighted: new this.THREE.Color(this.color.highlighted),
      default: new this.THREE.Color(this.color.default),
      erase: new this.THREE.Color(this.color.erase)
    }

    this.facesMode = 'erase'
    this.mouse = new this.THREE.Vector2()

    if (!Detector.webgl) Detector.addGetWebGLMessage()

    this.params = {
      printer_type: this.PRINTER,
      critical_angle: this.CONFIG.critical_angle[0],
      hsub: this.CONFIG.hsub[0],
      scale_factor: this.CONFIG.scaling[0],
      fetch_support: false,
      units: this.CONFIG.unit,
      Rotate_about_X: 0,
      Rotate_about_Y: 0,
      Rotate_about_Z: 0,
      orientation_mode: true,
      supportStructures: false,
      brushMode: true,
      brushRadius: 1,
      brushBothSide: false,
      selectionTolerance: 1.5,
      restrictOrientation: this.CONFIG.restrictOrientation,
      // restrictFaces: this.CONFIG.restrictFaces,
      restrictFaces: true,
      Restrict_rotation_X: 180,
      Restrict_rotation_Y: 180,
      Restrict_rotation_Z: 180,
      supportTransparency: this.uploadedSupports ? 0.7 : 0.2
    }
    if (this.isNested) {
      this.params.allowZRotation = this.allowZRotation
      this.params.currentPartName = this.currentPartName
      if (this.isManualPlacement) {
        this.params.currentPartOrPlateName = this.currentPartName
        this.params.selectedPartName = ''
        this.params.partsAndPlatesNames = [...this.nestedPartsNames]
      }
    }
    if (this.CONFIG.unit_supp) {
      this.params.unit_supp = this.CONFIG.unit_supp
    }
    document.body.style.margin = '0'
    document.body.style.overflow = 'hidden'
  }

  init () {
    this.preInitializer()
    this.scene = new this.THREE.Scene()
    this.scene.fog = new this.THREE.Fog(0xd4d4d4, 0.01, 4000)
    this.renderer = new this.THREE.WebGLRenderer({antialias: true, alpha: true})
    this.renderer.setClearColor(this.scene.fog.color)
    // renderer.setClearColor(0x000000, 0)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    document.body.appendChild(this.renderer.domElement)

    // Camera
    this.camera = new this.THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 30, 10000)
    this.camera.up.set(0, 0, 1)
    if (this.PRINTER_CONFIG_LIBRARY[this.PRINTER]) {
      this.camera.position.set(...this.PRINTER_CONFIG_LIBRARY[this.PRINTER].position)
    } else if (this.PRINTERS_CONFIG[this.PRINTER]) {
      const printerConfig = this.PRINTERS_CONFIG[this.PRINTER]
      this.camera.position.set(
        printerConfig.plateX * -0.9,
        printerConfig.plateY * -1.2,
        printerConfig.plateZ,
      )
    } else {
      this.camera.position.set(
        this.CONFIG.PrinterXYZ[0] * -0.9,
        this.CONFIG.PrinterXYZ[1] * -1.2,
        this.CONFIG.PrinterXYZ[2]
      )
    }
    this.scene.add(this.camera)

    // Raycaster
    this.ray = new this.THREE.Raycaster()

    // Light
    // scene.add(new this.THREE.AmbientLight(0x222222))
    // var light = new this.THREE.PointLight(0xffffff, 1)
    // camera.add(light)
    this.threePointLight()
    this.scene.add(new this.THREE.AmbientLight(0x333333))

    this.groupBase = new this.THREE.Group()
    this.drawGround()
    this.drawPrinter()
    this.groupPart = new this.THREE.Group()
    this.groupPart.rotation.order = 'ZYX'
    this.groupSupp = new this.THREE.Group()
    var material = new this.THREE.MeshPhongMaterial({
      color: 0xffffff,
      specular: 0x111111,
      shininess: 200,
      vertexColors: this.THREE.VertexColors
    })
    if (this.isNested) {
      this.STL_FILES.forEach((part, index) => {
        const name = part.match(/[A-Za-z0-9-._()]+\.stl/)[0]
        this['nestedPart' + name] = new this.THREE.Group()
        const forcedMesh = index === 0
        this.STLList.push(this.STL_LOAD(this['nestedPart' + name], part, material, forcedMesh, true))
      })
      this.transformControl = new this.THREE.TransformControls(this.camera, this.renderer.domElement)
      this.scene.add(this.transformControl)
    } else {
      this.STLList.push(this.STL_LOAD(this.groupPart, this.STL_FILES[0], material, true, true))
    }
    const initScale = this.params.scale_factor * this.unit2mmConstant(this.CONFIG.unit)
    const setScale = (group) => group.scale.set(initScale, initScale, initScale)
    if (!this.isNested) {
      setScale(this.groupPart)
    } else {
      const group = this['nestedPart' + this.currentPartName]
      setScale(group)
    }
    let materialSupp = new this.THREE.MeshPhongMaterial({color: 0x8e44ad, side: this.THREE.DoubleSide})

    if (this.uploadedSupports) {
      materialSupp.transparent = true
      this.STL_SUPPORT_FILES.forEach((fileSupports) => {
        this.STLList.push(this.STL_LOAD(this.groupSupp, fileSupports, materialSupp, false, true))
      })
      const initSuppScale = this.unit2mmConstant(this.CONFIG.unit_supp)
      this.groupSupp.scale.set(initSuppScale, initSuppScale, initSuppScale)
    }

    // Controls

    // controls = new this.THREE.OrbitControls(camera, renderer.domElement)
    // // controls.minDistance = 20
    // // controls.maxDistance = 50
    // // controls.maxPolarAngle = Math.PI / 2
    // controls.addEventListener('change', render)
    // controls.target.set(0, 1.2, 2)
    // controls.update()
    this.controls = new this.THREE.TrackballControls(this.camera, this.renderer.domElement)
    this.controls.rotateSpeed = 5.0
    this.controls.zoomSpeed = 5
    this.controls.panSpeed = 2
    this.controls.noZoom = false
    this.controls.noPan = true
    this.controls.staticMoving = true
    this.controls.noRotate = true
    this.controls.dynamicDampingFactor = 0.3
    this.controls.enabled = true

    var handle = new this.GUI_SETUP(this)
    this.gui = new this.dat.GUI({width: 350})
    var folderGeometry = this.gui.addFolder('Input Viewer')
    if (this.isNested) {
      var folderJobList = this.gui.addFolder('Nested parts')
    }
    this.setTextGray = (el, value) => {
      el.__li.style.color = value ? 'gray' : ''
    }

    this.toggleControlDisabled = (el, isDisableNext = false) => {
      const isDisableNow = el.domElement.style.pointerEvents
      const toggle = (disable) => {
        el.domElement.style.pointerEvents = disable ? 'none' : ''
        el.domElement.style.opacity = disable ? 0.5 : 1
        this.setTextGray(el, isDisableNext)
      }
      if (isDisableNow && isDisableNext) return
      else if (!isDisableNow && !isDisableNext) return
      else toggle(isDisableNext)
    }
    this.disableList = (el, value) => {
      this.setTextGray(el, value)
      el.__select.disabled = value
      this.toggleControlDisabled(el, value)
    }
    this.disableCheckbox = (el, value) => {
      this.toggleControlDisabled(el, value)
      el.__checkbox.disabled = value
    }
    this.switchGuiToolsDisable = () => {
      let disable = !this.params.orientation_mode
      let disableOrientModel = this.params.supportStructures
      this.toggleControlDisabled(this._scale, disable)
      this.toggleControlDisabled(this._x, disable)
      this.toggleControlDisabled(this._y, disable)
      this.toggleControlDisabled(this._z, disable)
      this.disableCheckbox(this._orientation_mode, disableOrientModel)
      this.disableList(this._units, disable)
      if (this._z_rotation_access) {
        this.disableCheckbox(this._z_rotation_access, disable)
      }
      if (this.isManualPlacement) {
        this.disableList(this._add_part, disable)
        this.setTextGray(this._remove_part, disable)
        this.setTextGray(this._remove_plate, disable)
        this.setTextGray(this._add_plate, disable)
        this.togglePlacementMode(this.isManualPlacementMode)
      }
    }
    this.togglePlacementMode = (value) => {
      this.isManualPlacementMode = value
      if (!this.params.orientation_mode) return
      const mode = value ? 'translate' : 'rotate'
      this.toggleControlDisabled(this._scale, value)
      this.toggleControlDisabled(this._x, value)
      this.toggleControlDisabled(this._y, value)
      this.setTextGray(this._remove_part, !value)
      this.setTextGray(this._remove_plate, !value)
      this.disableList(this._units, value)
      this.disableList(this._add_part, !value)
      this.transformControl.setMode(mode)
      this.transformControl.showZ = !value
    }

    const toggleSupportStructures = (value) => { // eslint-disable-line no-unused-vars
      if (value) {
        this._orientation_mode.setValue(false)
        this.jQuery('input', this.jQuery('.dg .cr.boolean')[0]).prop('disabled', true)
        // set baseplate
        var bbox = new this.THREE.Box3().setFromObject(this.groupPart)
        var modelBase = bbox.min.z
        this.groupPart.position.z = this.groupPart.position.z - modelBase + this.params.hsub
        var center = bbox.getCenter()
        this.groupPart.position.x = this.groupPart.position.x - center.x
        this.groupPart.position.y = this.groupPart.position.y - center.y
        // groupPart.position.x(center.x * (-1.0), center.y * (-1.0), 0)
        // create_supports()
        // Calculate the supports.
        this.supportLoading(true)
        setTimeout(() => {
          this.support.draw(this.scene, this.groupPart, {
            criticalAngle: this.params.critical_angle,
            restrictedFaces: this.restrictedFaces,
            allowedFaces: this.allowedFaces,
            supportConfig: this.CONFIG
          })
          this.supportLoading(false)
        }, 50)
      } else {
        this.jQuery('input', this.jQuery('.dg .cr.boolean')[0]).prop('disabled', false)
        this.params.orientation_mode = false
        this._orientation_mode.updateDisplay()
        this.support.clear(this.scene)
      }
    }
    this.toggleSupportStructuresExternal = (value) => {
      if (value) {
        this.jQuery('input', this.jQuery('.dg .cr.boolean')[0]).prop('disabled', true)
        // set baseplate
        var bbox = new this.THREE.Box3().setFromObject(this.groupPart)
        var modelBase = bbox.min.z
        this.groupPart.position.z = this.groupPart.position.z - modelBase + this.params.hsub
        var center = bbox.getCenter()
        this.groupPart.position.x = this.groupPart.position.x - center.x
        this.groupPart.position.y = this.groupPart.position.y - center.y
        // Calculate the supports.
        this.supportLoading(true)
        this.generateSupportExternal({
          rotation: [this.params.Rotate_about_X, this.params.Rotate_about_Y, this.params.Rotate_about_Z],
          scaling: [this.params.scale_factor],
          units: this.params.units,
          AllowedFaces: this.allowedFaces,
          RestrictedFaces: this.restrictedFaces
        })
          .then((payload) => {
            if (this.groupSupp.children.length > 0) {
              this.groupSupp.remove(this.groupSupp.children[0])
            }
            this.processSupportsLoading(payload.data.url, 0)
          })
      } else {
        this.jQuery('input', this.jQuery('.dg .cr.boolean')[0]).prop('disabled', false)
        this.params.orientation_mode = false
        this.groupSupp.position.x = 0
        this.groupSupp.position.y = 0
        this._orientation_mode.updateDisplay()
        this.scene.remove(this.groupSupp)
      }
    }
    this.processSupportsLoading = (supportsUrl, attempt) => {
      this.STL_LOAD(this.groupSupp, supportsUrl, materialSupp, false, true)
        .then(() => {
          const bbox = new this.THREE.Box3().setFromObject(this.groupPart)
          this.groupSupp.position.x = this.groupSupp.position.x + bbox.min.x
          this.groupSupp.position.y = this.groupSupp.position.y + bbox.min.y
          for (let i = 0; i < this.groupSupp.children.length; i++) {
            this.groupSupp.children[i].material.opacity = this.params.supportTransparency
            this.groupSupp.children[i].material.transparent = true
          }
          this.scene.add(this.groupSupp)
          this.supportLoading(false)
        })
        .catch((err) => {
          if (err.currentTarget &&
            err.currentTarget.status &&
            err.currentTarget.status === 404 && attempt < 30) {
            attempt++
            let delay = 2000 * attempt
            if (attempt > 3) {
              delay = 15000
            }
            setTimeout(() => {
              this.processSupportsLoading(supportsUrl, attempt)
            }, delay)
          } else {
            this.supportLoading(false)
            this.toggleSupportStructuresExternal(false)
            this._supports_mode.setValue(false)
          }
        })
    }
    this.getCurvaturePerFace = () => {
      this.curvatureLoading(true)
      this.getCurvaturePerFaceData()
        .then((data) => {
          this.curvaturePerFacet = data.curvature_per_facet
          this.facetAdjacents = data.triangles_adjacent_triangle
          for (let i = 0; i < this.curvaturePerFacet.length; i++) {
            if (this.curvaturePerFacet[i] > this.maxCurvature) {
              this.maxCurvature = this.curvaturePerFacet[i]
            }
          }
          this.curvatureLoading(false)
        })
        .catch((err) => {
          this.curvatureLoading(false)
          this._brushMode.setValue(true)
          console.log(err)
        })
    }
    this.changeSupportTransparency = (value) => {
      for (let i = 0; i < this.groupSupp.children.length; i++) {
        this.groupSupp.children[i].material.opacity = value
      }
    }
    this.applyRotation = () => {
      let group = this.groupPart
      if (this.isNested) {
        if (!this.isManualPlacementMode) group = this['nestedPart' + this.currentPartName]
        else group = this.currentObject
      }
      if (this.params.orientation_mode) {
        this.STL_ROTATE(group)
        this.centeredPosition(group)
      }
    }
    if (!this.uploadedSupports) {
      const toggleOrientModel = (value) => {
        const control = this.isNested ? this.transformControl : this.myquart
        if (this.params.supportStructures) {
          this.params.orientation_mode = false
          this._orientation_mode.updateDisplay()
          value = false
        }
        this.switchGuiToolsDisable()
        if (value) {
          // this.jQuery('select', this.jQuery('.dg .cr.string')[0]).prop('disabled', false)
          this.controls.noPan = true
          this.controls.noRotate = true
          control.enabled = true
          // enableAngleSet = true
          if (this.isNested && !this.isManualPlacementMode) {
            control.attach(this['nestedPart' + this.currentPartName])
          }
          this.support.clear(this.scene)
        } else {
          // this.jQuery('select', this.jQuery('.dg .cr.string')[0]).prop('disabled', true)
          this.controls.noPan = false
          this.controls.noRotate = false
          control.enabled = false
          if (this.isNested && !this.isManualPlacementMode) {
            control.detach(this['nestedPart' + this.currentPartName])
          }
          if (this.isManualPlacementMode) {
            this.saveAndRemoveCurrentObject()
          }
          // set baseplate
          const setPosition = (group) => {
            var bbox = new this.THREE.Box3().setFromObject(group)
            var modelBase = bbox.min.z
            group.position.z = group.position.z - modelBase + this.params.hsub
            var center = bbox.getCenter()
            group.position.x = group.position.x - center.x
            group.position.y = group.position.y - center.y
          }
          if (!this.isNested) {
            setPosition(this.groupPart)
          } else if (!this.isManualPlacementMode) {
            const group = this['nestedPart' + this.currentPartName]
            setPosition(group)
          }
          // groupPart.position.x(center.x * (-1.0), center.y * (-1.0), 0)
          // create_supports()
          // Calculate the supports.
          // this.supportLoading(true)
          // setTimeout(() => {
          //   this.support.draw(this.scene, this.groupPart, {
          //     criticalAngle: this.params.critical_angle,
          //     restrictedFaces: this.restrictedFaces,
          //     allowedFaces: this.allowedFaces,
          //     supportConfig: this.CONFIG
          //   })
          //   this.supportLoading(false)
          // }, 50)
        }
      }
      this._orientation_mode = folderGeometry.add(this.params, 'orientation_mode').name('Orient Model')
      this._orientation_mode.onChange(toggleOrientModel)
      if (!this.isNested) {
        this._supports_mode = folderGeometry.add(this.params, 'supportStructures').name('Support structures')
        this._supports_mode.onChange((value) => {
          if (this.params.orientation_mode && value) {
            toggleOrientModel(false)
          }
          this.switchGuiToolsDisable()
          this.toggleSupportStructuresExternal(value)
        })
        this._supports_transparency = folderGeometry
          .add(this.params, 'supportTransparency', 0, 1)
          .step(0.01)
          .name('Support transparency')
          .onChange((value) => {
            if (this.params.supportStructures && !this.isNested) {
              this.changeSupportTransparency(value)
            } else return
          })
        folderGeometry.add(handle, 'generateSupports').name('Generate support')
      }
      const setScale = (group, scaleFactor) => group.scale.set(scaleFactor, scaleFactor, scaleFactor)
      this._units = folderGeometry.add(this.params, 'units').options({
        'Millimeters': 'mm',
        'Centimeters': 'cm',
        'Meters': 'm',
        'Inches': 'inch'
      }).name('Unit').onChange((value) => {
        if (this.params.orientation_mode) {
          this.CONFIG.unit = value
          const scaleFactor = this.params.scale_factor * this.unit2mmConstant(value)
          if (!this.isNested) {
            setScale(this.groupPart, scaleFactor)
          } else if (!this.isManualPlacementMode) {
            const group = this['nestedPart' + this.currentPartName]
            setScale(group, scaleFactor)
          }
        }
      })
      this._scale = folderGeometry.add(this.params, 'scale_factor', 0.001, 20).step(0.001).name('Scaling factor of part')
      this._scale.onChange((value) => {
        if (this.params.orientation_mode) {
          const scaleFactor = value * this.unit2mmConstant(this.CONFIG.unit)
          if (!this.isNested) {
            setScale(this.groupPart, scaleFactor)
          } else {
            const group = this['nestedPart' + this.currentPartName]
            setScale(group, scaleFactor)
          }
        }
      })
      this._x = folderGeometry.add(this.params, 'Rotate_about_X', -180, 180).step(1).name('x rotation \u00B0').onChange(() => this.applyRotation())
      this._y = folderGeometry.add(this.params, 'Rotate_about_Y', -180, 180).step(1).name('y rotation \u00B0').onChange(() => this.applyRotation())

      if (this.isNested && !this.isManualPlacement) {
        const getController = (name) => folderGeometry.__controllers.find(({property}) => property === name)
        this._z_rotation_access = folderGeometry.add(this.params, 'allowZRotation').name('Set z rotation manually').listen()
        this._z_rotation_access.onChange((value) => {
          if (!this.params.orientation_mode) return
          this.changeZRotationAccess(value)
        })
        this._z = folderGeometry.add(this.params, 'Rotate_about_Z', -180, 180).step(1).name('z rotation \u00B0').onChange(() => this.applyRotation())
        if (!this.allowZRotation) {
          getController('Rotate_about_Z').__li.style.display = 'none'
        }
      }
      if (!this.isNested || (this.isNested && this.isManualPlacement)) {
        this._z = folderGeometry.add(this.params, 'Rotate_about_Z', -180, 180).step(1).name('z rotation \u00B0').onChange(() => this.applyRotation())
      }
      if (this.isNested) {
        const changeRotation = (axis, value) => {
          this.params[`Rotate_about_${axis.toUpperCase()}`] = value
          this[`_${axis}`].updateDisplay()
        }
        this.refreshGuiData = (config) => {
          if (!config) {
            config = {RotationalAngle: [0, 0, 0], scaling: [1], unit: 'Millimeters'}
          }
          const {RotationalAngle, scaling, unit} = config
          changeRotation('x', RotationalAngle[0])
          changeRotation('y', RotationalAngle[1])
          changeRotation('z', RotationalAngle[2])
          this.params.scale_factor = scaling[0]
          this.params.units = unit
          this._scale.updateDisplay()
        }

        const setupNewPart = (newName) => {
          const newPartGroup = this['nestedPart' + newName]
          const newPartConfig = this.partsConfig[newName]
          this.refreshGuiData(newPartConfig)
          if (this.params.orientation_mode) {
            this.transformControl.attach(newPartGroup)
          }
          this.scene.add(newPartGroup)
          this.currentPartName = newName
        }
        const saveAndRemovePart = () => {
          const currentData = handle.getData()
          const currentGroup = this['nestedPart' + this.currentPartName]
          this.transformControl.detach(currentGroup)
          this.updateConfig(currentData, this.partsConfig[this.currentPartName])
          this.refreshGuiData()
          this.scene.remove(currentGroup)
          this.currentPartName = ''
        }

        const checkOldParam = (newName, config) => {
          if (!this.old[newName]) {
            this.old[newName] = {
              scale: config.scaling[0],
              supportTransparency: 0.2
            }
          }
        }

        const saveAndRemovePlate = () => {
          const plateGroup = this.getPlateGroup()
          this.saveAndRemoveCurrentObject()
          this.togglePrintBoxVisible(false)
          this.scene.remove(plateGroup)
          this.currentPlateName = ''
        }

        const recreatePlate = (plateName) => {
          this.currentPlateName = plateName
          const pInx = this.getIndex()
          const plate = this.platesConfig[pInx]
          const plateGroup = new this.THREE.Group()
          plateGroup.name = plateName
          for (let partName in plate) {
            for (let i = 0; i < plate[partName].length; i += 3) {
              const cloneGroup = this['nestedPart' + partName].clone()
              cloneGroup.name = `${partName}_copy_${i / 3 + 1}`
              this.setCloneRotPosFromConfig(cloneGroup)
              this.centeredPosition(cloneGroup)
              plateGroup.add(cloneGroup)
            }
          }
          this.scene.add(plateGroup)
          this.refreshGuiData()
          this.checkOverlaps()
        }
        this.refreshList = (el, options, name, onChange) => {
          this[el] = this[el].options(options).name(name).onChange(onChange)
          let elements = document.getElementsByClassName('ui-tooltip')
          while (elements.length > 0) {
            elements[0].parentNode.removeChild(elements[0])
          }
          this.initLegend()
        }
        this.onAddingPlate = () => {
          if (!this.params.orientation_mode) return
          const count = this.platesConfig.length
          const newName = `plate_${count + 1}`
          this.platesConfig.push({})
          this.oldPosition.push([])
          if (this.currentPartName) {
            this.togglePlacementMode(true)
            saveAndRemovePart()
            this.refreshGuiData()
          } else saveAndRemovePlate()
          const plateGroup = new this.THREE.Group()
          plateGroup.name = newName
          this.scene.add(plateGroup)
          this.params.partsAndPlatesNames.push(newName)
          this.params.currentPartOrPlateName = newName
          this.currentPlateName = newName
          this.refreshList('_part_or_plate', this.params.partsAndPlatesNames, 'Parts and Plates', onPartOrPlateChange)
        }

        this.onSelectPart = (partName) => {
          this.saveAndRemoveCurrentObject()
          const cloneGroup = this['nestedPart' + partName].clone()
          const plateGroup = this.getPlateGroup()
          const pInx = this.getIndex()
          const {x, y} = this.toPrinterZero(0, 0)
          const count = this.platesConfig[pInx][partName] ? this.platesConfig[pInx][partName].length / 3 : 0
          if (!count) {
            this.platesConfig[pInx][partName] = [x, y, 0]
            this.oldPosition[pInx] = {...this.oldPosition[pInx], [partName]: [0, 0, 0]}
          } else {
            this.platesConfig[pInx][partName].push(x, y, 0)
            this.oldPosition[pInx][partName].push(0, 0, 0)
          }
          cloneGroup.name = `${partName}_copy_${count + 1}`
          this.params.selectedPartName = ''
          this.refreshList('_add_part', this.nestedPartsNames, 'Add part to Build Plate', this.onSelectPart)
          plateGroup.add(cloneGroup)
          this.transformControl.attach(cloneGroup)
          this.currentObject = cloneGroup
          this.centeredPosition(cloneGroup)
          this.setCloneRotPosFromConfig()
          this.checkOverlaps()
        }
        const onPartChange = (newName) => {
          saveAndRemovePart()
          setupNewPart(newName)
          checkOldParam(newName, this.partsConfig[newName])
        }
        const onPartOrPlateChange = (newName) => {
          const isNewThingPlate = newName.match(this.plateMatch)
          if (this.currentPartName) saveAndRemovePart()
          else {
            this.togglePlacementMode(false)
            saveAndRemovePlate()
          }
          if (!isNewThingPlate) {
            this.togglePlacementMode(false)
            setupNewPart(newName)
            checkOldParam(newName, this.partsConfig[newName])
          } else {
            this.togglePlacementMode(true)
            recreatePlate(newName)
          }
        }
        this.onRemoveClone = () => {
          if (!this.currentObject) return
          const cloneName = this.currentObject.name
          const pInx = this.getIndex()
          const cInx = this.getIndex(cloneName) * 3
          const partName = this.getPartName()
          const plateGroup = this.scene.getObjectByName(this.currentPlateName)
          const clones = this.platesConfig[pInx][partName]
          const oldClonesPos = this.oldPosition[pInx][partName]
          const newConf = []
          const newOldConf = []
          for (let i = 0; i < clones.length; i += 3) {
            if (i === cInx) continue
            const [x, y, zRot] = [clones[i], clones[i + 1], clones[i + 2]]
            const [xPos, yPos, zPos] = [oldClonesPos[i], oldClonesPos[i + 1], oldClonesPos[i + 2]]
            newConf.push(x, y, zRot)
            newOldConf.push(xPos, yPos, zPos)
          }
          plateGroup.remove(this.currentObject)
          const names = []
          plateGroup.children.forEach(child => {
            if (child.name.match(new RegExp(partName))) {
              const inx = names.length + 1
              child.name = `${partName}_copy_${inx}`
              names.push(1)
            }
          })
          this.saveAndRemoveCurrentObject()
          this.platesConfig[pInx][partName] = [...newConf]
          this.oldPosition[pInx][partName] = [...newOldConf]
          this.checkOverlaps()
        }
        this.getRemovePlateModal = () => {
          if (!this.isManualPlacementMode) return
          const isNotEmpyty = this.getPlateGroup().children.length
          if (isNotEmpyty) this.openRemovePlateModal()
          else this.onRemovePlate(true)
        }
        this.onRemovePlate = (value) => {
          if (!value) return
          const plateGroup = this.getPlateGroup()
          const pInx = this.getIndex()
          this.togglePrintBoxVisible(false)
          this.saveAndRemoveCurrentObject()
          this.scene.remove(plateGroup)
          const newNames = []
          const plates = []
          this.params.partsAndPlatesNames.forEach(name => {
            if (name === this.currentPlateName) return
            if (name.match(this.plateMatch)) {
              newNames.push(`plate_${plates.length + 1}`)
              plates.push(1)
            } else newNames.push(name)
          })
          this.params.partsAndPlatesNames = newNames
          const firstName = newNames[0]
          const newConfig = []
          const newOldConf = []
          for (let i = 0; i < this.platesConfig.length; i += 1) {
            if (i === pInx) continue
            newConfig.push(this.platesConfig[i])
            newOldConf.push(this.oldPosition[i])
          }
          this.platesConfig = newConfig
          this.oldPosition = newOldConf
          this.currentPlateName = ''
          this.params.currentPartOrPlateName = firstName
          this.refreshList('_part_or_plate', this.params.partsAndPlatesNames, 'Parts and Plates', onPartOrPlateChange)
          this.togglePlacementMode(false)
          setupNewPart(firstName)
        }

        if (!this.isManualPlacement) {
          folderJobList.add(this.params, 'currentPartName').options(this.nestedPartsNames).name('List of Parts').onChange(onPartChange)
        } else {
          this._part_or_plate = folderJobList.add(this.params, 'currentPartOrPlateName').options(this.params.partsAndPlatesNames).name('Parts and plates').onChange(onPartOrPlateChange)
          this._add_plate = folderJobList.add(this, 'onAddingPlate').name('Add new build plate')
          this._add_part = folderJobList.add(this.params, 'selectedPartName').options(this.nestedPartsNames).name('Add part to build plate').onChange(this.onSelectPart)
          this._remove_part = folderJobList.add(this, 'onRemoveClone').name('Remove selected part')
          this._remove_plate = folderJobList.add(this, 'getRemovePlateModal').name('Remove current plate')
          this.togglePlacementMode(false)
        }
        folderJobList.open()
      }
    } else {
      if (!this.CONFIG.isHideSupports) {
        folderGeometry
          .add(this.params, 'supportTransparency', 0, 1)
          .step(0.01)
          .name('Support transparency')
          .onChange(this.changeSupportTransparency)
      }
      folderGeometry.add(this.params, 'units').options({
        'Millimeters': 'mm',
        'Centimeters': 'cm',
        'Meters': 'm',
        'Inches': 'inch'
      }).name('Unit').onChange((value) => {
        // this.params.units = value
        this.CONFIG.unit = value
        const scaleFactor = this.params.scale_factor * this.unit2mmConstant(value)
        this.groupPart.scale.set(scaleFactor, scaleFactor, scaleFactor)
      })
      if (!this.CONFIG.isHideSupports) {
        folderGeometry.add(this.params, 'unit_supp').options({
          'Millimeters': 'mm',
          'Centimeters': 'cm',
          'Inches': 'inch'
        }).name('Supports Unit').onChange((value) => {
          // this.params.units = value
          this.CONFIG.unit_supp = value
          const scaleFactor = this.unit2mmConstant(value)
          this.groupSupp.scale.set(scaleFactor, scaleFactor, scaleFactor)
          var bbox = new this.THREE.Box3().setFromObject(this.groupSupp)
          var modelBase = bbox.min.z
          this.groupSupp.position.z = this.groupSupp.position.z - modelBase
          var bboxPart = new this.THREE.Box3().setFromObject(this.groupPart)
          var center = bboxPart.getCenter()
          this.groupSupp.position.x = this.groupPart.position.x - center.x
          this.groupSupp.position.y = this.groupPart.position.y - center.y
        })
      }
      this.controls.noPan = false
      this.controls.noRotate = false
    }
    folderGeometry.add(handle, 'reset').name('Reset View')
    folderGeometry.add(handle, 'save').name('RUN JOB')
    folderGeometry.add(handle, 'help').name('Get Help')
    folderGeometry.open()
    if (this.params.restrictFaces && !this.uploadedSupports && !this.isNested) {
      // Changing this stop the legend working
      var folderFacet = this.gui.addFolder('Restrict or allow support generation on facets')
      // Changing this stop the legend working [end]
      this._brushMode = folderFacet.add(this.params, 'brushMode').name('Brush mode').onChange((value) => {
        this.params.brushMode = value
        if (!value && this.curvaturePerFacet.length === 0) {
          this.getCurvaturePerFace()
        }
      })
      folderFacet.add(this.params, 'brushRadius', 0, 5).step(0.1).name('Brush radius').onChange((value) => {
        this.params.brushRadius = value
      })
      folderFacet.add(this.params, 'brushBothSide').name('Brush paints on both sides').onChange((value) => {
        this.params.brushBothSide = value
      })
      folderFacet.add(this.params, 'selectionTolerance', 0, 5).step(0.1).name('Tolerance').onChange((value) => {
        this.params.selectionTolerance = value
      })
      folderFacet.add(this, 'facesMode').options({'Restrict Support Attachment': 'restrict', 'Allow Support Attachment': 'allow', 'Erase Support Attachment': 'erase'}).name('Facets selection')
      folderFacet.add(handle, 'selectAll').name('Select All')
      folderFacet.add(handle, 'resetFaces').name('Reset selection')
      folderFacet.open()
    }
    if (this.params.restrictOrientation && !this.uploadedSupports) {
      var folderOrientation = this.gui.addFolder('Add degrees of freedom for the orientation on the left')
      this._xRestrict = folderOrientation.add(this.params, 'Restrict_rotation_X', 0, 180).step(1).name('Restrict x rotation \u00B0')
      this._yRestrict = folderOrientation.add(this.params, 'Restrict_rotation_Y', 0, 180).step(1).name('Restrict y rotation \u00B0')
      this._zRestrict = folderOrientation.add(this.params, 'Restrict_rotation_Z', 0, 180).step(1).name('Restrict z rotation \u00B0')
      folderOrientation.open()
    }

    this.initLegend = this.initLegend.bind(this)
    this.initLegend()

    this.setupInset()
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onDocumentMouseClick = this.onDocumentMouseClick.bind(this)
    this.onDocumentMouseMove = this.onDocumentMouseMove.bind(this)
    this.onDocumentMouseUp = this.onDocumentMouseUp.bind(this)
    this.onDocumentContextMenu = this.onDocumentContextMenu.bind(this)
    this.colorFaces = this.colorFaces.bind(this)
    window.addEventListener('resize', this.onWindowResize)

    Promise.all(this.STLList).then(() => {
      this.STL_LOAD_FINISHED()
    })
  }
  changeAllowZRotation (value) {
    this.allowZRotation = value
    this.params.allowZRotation = value
    const controller = this.gui.__folders['Input Viewer'].__controllers.find(({property}) => property === 'Rotate_about_Z')
    const checkbox = this.gui.__folders['Input Viewer'].__controllers.find(({property}) => property === 'allowZRotation')
    checkbox.__checkbox.checked = value
    checkbox.__prev = value
    checkbox.updateDisplay()
    this.transformControl.showZ = value
    const group = this['nestedPart' + this.currentPartName]
    if (this.allowZRotation) {
      controller.__li.style.display = ''
    } else {
      group.rotation.z = 0
      this.params.Rotate_about_Z = this.rad2deg(group.rotation.z)
      this._z.updateDisplay()
      controller.__li.style.display = 'none'
    }
  }
  initLegend () {
    this.jQuery('.dg.main').click((event) => {
      event.stopPropagation()
    })
    var $folders = this.jQuery('.dg.main.a ul li.folder')
    var $mainParams
    var $secondParams
    var $restriction
    var $restrictionSupport
    if ($folders.length > 1 && !this.isNested) {
      if ($folders.length === 3) {
        $restriction = $folders[2]
        $restrictionSupport = $folders[1]
      } else {
        var $checkFolder = this.jQuery($folders[1]).find('li')
        if ($checkFolder[0].innerHTML === 'Restrict or allow support generation on facets') {
          $restrictionSupport = $folders[1]
        } else {
          $restriction = $folders[1]
        }
      }
    }
    $mainParams = $folders[0]
    $secondParams = $folders[1]

    if ($secondParams && this.isManualPlacement) {
      const paramsList = this.jQuery($secondParams).find('li')
      paramsList.each((index, element) => {
        switch (index) {
          case 0:
            break
          case 1:
            this.jQuery(element).attr('title', 'By selecting part you can change its rotation. By selecting plate you can manually change position or rotation about z of each part that placed on current plate')
            break
          case 2:
            this.jQuery(element).attr('title', 'Add new build plate to start your manual placement')
            break
          case 3:
            this.jQuery(element).attr('title', 'Select part which copy you want add to build plate')
            break
          case 4:
            this.jQuery(element).attr('title', 'You need to select copy on build plate first to remove it')
            break
          case 5:
            this.jQuery(element).attr('title', 'You can remove current build plate. All copies on it will be removed also.')
            break
          default:
            break
        }
      })
    }

    if ($mainParams) {
      const paramsList = this.jQuery($mainParams).find('li')
      paramsList.each((index, element) => {
        if (this.uploadedSupports) {
          switch (index) {
            case 0:
              break
            case 1:
              this.jQuery(element).attr('title', 'Adjust support transparency.')
              break
            case 2:
              this.jQuery(element).attr('title', 'If you selected the wrong unit of measure, simply select the correct unit of measure from the dropdown.')
              break
            case 3:
              this.jQuery(element).attr('title', 'If you selected the wrong unit of measure, simply select the correct unit of measure from the dropdown.')
              break
            case 4:
              this.jQuery(element).attr('title', '"Reset view" returns the viewer to the default state that was first presented to you.')
              break
            case 5:
              this.jQuery(element).attr('title', '"RUN JOB" will save your orientation and generate your oriented part and support structure files.')
              break
            case 6:
              // Get help
              break
          }
        } if (this.isNested) {
          const isRunJob = paramsList.length - 2 === index
          const isReset = paramsList.length - 3 === index
          const isZRotation = paramsList.length - 4 === index
          if (isRunJob) {
            this.jQuery(element).attr('title', '"RUN JOB" will save your orientation and generate your oriented part and support structure files.')
          }
          if (isReset) {
            this.jQuery(element).attr('title', '"Reset view" returns the viewer to the default state that was first presented to you.')
          }
          if (isZRotation) {
            this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
          }
          switch (index) {
            case 0:
              break
            case 1:
              this.jQuery(element).attr('title', 'Uncheck this checkbox to freeze your orientation.')
              break
            case 2:
              this.jQuery(element).attr('title', 'If you selected the wrong unit of measure, simply select the correct unit of measure from the dropdown.')
              break
            case 3:
              this.jQuery(element).attr('title', 'The scaling factor for your STL file may be changed by sliding the blue bar left or right or manually entering the desired scaling factor to the right of the slider.')
              break
            case 4:
              this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
              break
            case 5:
              this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
              break
            case 6:
              this.jQuery(element).attr('title', 'By setting the z orientation manually, you may not achieve the best possible density of parts, check the box again to let AM Build Optimizer maximize your part density')
              break
            default:
              break
          }
        } else {
          switch (index) {
            case 0:
              break
            case 1:
              this.jQuery(element).attr('title', 'Uncheck this checkbox to freeze your orientation.')
              break
            case 2:
              this.jQuery(element).attr('title', 'Check this checkbox to view the support structures AM Build Optimizer™ will create for you.')
              break
            case 3:
              this.jQuery(element).attr('title', 'Adjust support transparency.')
              break
            case 4:
              this.jQuery(element).attr('title', 'Create support structures by AM Build Optimizer™.')
              break
            case 5:
              this.jQuery(element).attr('title', 'If you selected the wrong unit of measure, simply select the correct unit of measure from the dropdown.')
              break
            case 6:
              this.jQuery(element).attr('title', 'The scaling factor for your STL file may be changed by sliding the blue bar left or right or manually entering the desired scaling factor to the right of the slider.')
              break
            case 7:
              this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
              break
            case 8:
              this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
              break
            case 9:
              this.jQuery(element).attr('title', 'You may enter positive or negative numerical values to change the orientation of your part.')
              break
            case 10:
              this.jQuery(element).attr('title', '"Reset view" returns the viewer to the default state that was first presented to you.')
              break
            case 11:
              this.jQuery(element).attr('title', '"RUN JOB" will save your orientation and generate your oriented part and support structure files.')
              break
            case 12:
              // Get help
              break
            default:
              break
          }
        }
      })
    }

    if ($restriction) {
      var $restrictionList = this.jQuery($restriction).find('li')
      $restrictionList.each((index, element) => {
        switch (index) {
          case 0:
            break
          case 1:
            this.jQuery(element).attr('title', 'Once you set a baseline orientation, you may restrict our optimization to a specified number of degrees of freedom from that baseline orientation. Simply enter positive values indicating the degrees of freedom for each axis of rotation.')
            break
          case 2:
            this.jQuery(element).attr('title', 'Once you set a baseline orientation, you may restrict our optimization to a specified number of degrees of freedom from that baseline orientation. Simply enter positive values indicating the degrees of freedom for each axis of rotation.')
            break
          case 3:
            this.jQuery(element).attr('title', 'Once you set a baseline orientation, you may restrict our optimization to a specified number of degrees of freedom from that baseline orientation. Simply enter positive values indicating the degrees of freedom for each axis of rotation.')
            break
          default:
            break
        }
      })
    }

    if ($restrictionSupport) {
      var $restrictionSupportList = this.jQuery($restrictionSupport).find('li')
      $restrictionSupportList.each((index, element) => {
        switch (index) {
          case 0:
            break
          case 1:
            this.jQuery(element).attr('title', 'You can make it easier to select multiple STL faces by checking the "Brush mode" box and increasing the size of the "Brush radius".')
            break
          case 2:
            this.jQuery(element).attr('title', 'You can make it easier to select multiple STL faces by checking the "Brush mode" box and increasing the size of the "Brush radius".')
            break
          case 3:
            this.jQuery(element).attr('title', 'If checked, brush will paint both (inside and outside) sides of the hollowed part.')
            break
          case 4:
            this.jQuery(element).attr('title', 'Selection tolerance for non brush mode')
            break
          case 5:
            this.jQuery(element).attr('title', 'To restrict support structures, simply choose "Restrict Support Attachment" from the dropdown.')
            break
          case 6:
            this.jQuery(element).attr('title', 'Select all faces')
            break
          case 7:
            this.jQuery(element).attr('title', 'Clear all faces selections')
            break
          default:
            break
        }
      })
    }
    var jQ = this.jQuery
    this.jQuery('.dg.main.a').tooltip({
      position: {
        my: 'left center',
        at: 'right center',
        using: function (position, feedback) {
          jQ(this).css({
            'left': position.left - 10,
            'top': position.top
          })
          jQ('<div>')
            .addClass('arrow')
            .addClass(feedback.vertical)
            .addClass(feedback.horizontal)
            .appendTo(this)
        }
      }
    })
  }

  GUI_SETUP (context) {
    this.reset = () => {
      context.controls.reset()
    }
    this.resetFaces = () => {
      context.restrictedFaces = []
      context.allowedFaces = []
    }
    this.generateSupports = () => {
      context.toggleSupportStructuresExternal(false)
      context._supports_mode.setValue(true)
    }
    this.selectAll = () => {
      var i = 0
      if (context.facesMode === 'restrict') {
        context.allowedFaces = []
        for (i = 0; i < context.mesh.geometry.attributes.position.count / 3; i++) {
          context.restrictedFaces.push(i)
        }
      } else if (context.facesMode === 'allow') {
        context.restrictedFaces = []
        for (i = 0; i < context.mesh.geometry.attributes.position.count / 3; i++) {
          context.allowedFaces.push(i)
        }
      } else {
        this.resetFaces()
      }
    }
    this.save = () => {
      let data = this.getData()
      if (context.isNested && !context.isManualPlacement) {
        context.updateConfig(data, context.partsConfig[context.currentPartName])
        context.savePartsConfig(context.partsConfig)
      } else if (context.isManualPlacement) {
        context.savePartsConfig(context.partsConfig)
        context.savePlatesConfig(context.platesConfig)
      }
      context._saveCallback(data)
    }
    this.help = () => {
      context.showHelp()
    }
    this.getData = () => ({
      viewer: context,
      rotation: [context.params.Rotate_about_X, context.params.Rotate_about_Y, context.params.Rotate_about_Z],
      scaling: [context.params.scale_factor],
      facesRestrictions: {restrictedFaces: context.restrictedFaces, allowedFaces: context.allowedFaces},
      restriction: context.params.restrictOrientation ? [context.params.Restrict_rotation_X, context.params.Restrict_rotation_Y, context.params.Restrict_rotation_Z] : [],
      units: {part: context.params.units, support: context.params.unit_supp}
    })
  }
  STL_LOAD (groupx, file, material, forceMesh, isCalculateNormals) {
    return new Promise((resolve, reject) => {
      var loader = new this.THREE.STLLoader()
      loader.load(
        file,
        (geometry) => {
          let mesh = new this.THREE.Mesh(geometry, material)
          var scalexyz = 1
          mesh.scale.set(scalexyz, scalexyz, scalexyz)
          // calculate bounding box
          // mesh.geometry.computeBoundingBox()
          // var bbox = mesh.geometry.boundingBox
          // var width = bbox.max.x - bbox.min.x
          // var height = bbox.max.y - bbox.min.y
          // var depth = bbox.max.z - bbox.min.z
          // this.radius = Math.max(Math.max(Math.sqrt(width * width + height * height),
          //   Math.sqrt(width * width + depth * depth)), Math.sqrt(height * height + depth * depth)) / 2
          // var center = bbox.getCenter()
          // mesh.position.set(center.x * (-1.0), center.y * (-1.0), 0)
          mesh.castShadow = true
          mesh.receiveShadow = true
          groupx.add(mesh)
          if (forceMesh) {
            this.mesh = mesh
          }
          if (isCalculateNormals) {
            const normals = groupx.children[0].geometry.attributes.normal.array
            let needCalc = true
            for (let normal of normals) {
              if (normal !== 0) {
                needCalc = false
                break
              }
            }
            if (needCalc) groupx.children[0].geometry.computeVertexNormals()
          }
          resolve(geometry)
        },
        () => {}, // onProgress event
        (err) => {
          reject(err)
        })
    })
  }

  STL_LOAD_FINISHED () {
    if (this.isNested) {
      this.STL_FILES.forEach((plate, index) => {
        const name = plate.match(/[A-Za-z0-9-._()]+\.stl/)[0]
        this.colorChange(this['nestedPart' + name].children[0], this.colorObj.default)
        const group = this['nestedPart' + name]
        group.name = name
        group.rotation.order = 'ZYX'
        group.children[0].name = name
        group.children[0].geometry.center()
        this.centeredPosition(group)
        this.getMinMaxVerticles(group)
        if (name === this.currentPartName) {
          if (!this.allowZRotation && !this.isManualPlacement) this.transformControl.showZ = this.allowZRotation
          this.scene.add(group)
          this.transformControl.setMode('rotate')
          this.transformControl.attach(group)
        }
      })
    } else {
      this.colorChange(this.groupPart.children[0], this.colorObj.default)
      if (!this.uploadedSupports) {
        this.groupPart.children[0].geometry.center()
        this.myquart = new this.THREE.RotationWithQuaternion(this.groupPart, this.camera, this.renderer.domElement)
        this.myquart.enabled = true
      }
      this.scene.add(this.groupPart)
      this.centeredPosition(this.groupPart)
    }
    this.finishedLoading = true
    this.finishedLoadingCB()
    if (this.params.restrictFaces && !this.uploadedSupports) {
      window.addEventListener('click', this.onDocumentMouseClick)
      window.addEventListener('mousemove', this.onDocumentMouseMove)
    }
    window.addEventListener('mouseup', this.onDocumentMouseUp, true)
    window.addEventListener('contextmenu', this.onDocumentContextMenu)

    if (this.uploadedSupports) {
      var bbox = new this.THREE.Box3().setFromObject(this.groupPart)
      var center = bbox.getCenter()
      this.groupSupp.position.x = this.groupPart.position.x - center.x
      this.groupSupp.position.y = this.groupPart.position.y - center.y
      this.scene.add(this.groupSupp)
      this.changeSupportTransparency(this.params.supportTransparency)
    }
  }

  STL_ROTATE (group) {
    group.rotation.x = this.deg2rad(this.params.Rotate_about_X)
    group.rotation.y = this.deg2rad(this.params.Rotate_about_Y)
    group.rotation.z = this.deg2rad(this.params.Rotate_about_Z)
    group.rotation.order = 'ZYX'
  }

  light (color, {
    position, normalize = null, intensity = 0.5, castShadow = true
  }) {
    const light = new this.THREE.DirectionalLight(color)
    light.position.set(...position).normalize(normalize)
    light.intensity = intensity
    light.castShadow = castShadow
    return light
  }

  threePointLight () {
    const dist = 400
    this.scene.add(this.light(0xb8b8b8, { position: [1 * dist, 2 * dist, 1 * dist] }))
    this.scene.add(this.light(0xb8b8b8, { position: [-1 * dist, 0.6 * dist, 0.5 * dist], intensity: 2 }))
    this.scene.add(this.light(0xffffff, { position: [-0.3 * dist, -0.6 * dist, 0.8 * dist], normalize: 0xb8b8b8 }))
  }

  onWindowResize () {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }
  toPrinterZero (x, y, isToZero = true) {
    const bbox = this.scene.getObjectByName('printer').geometry.attributes.position.array
    let min = {x: 0, y: 0}
    for (let i = 0; i < bbox.length; i += 3) {
      const [x, y] = [bbox[i], bbox[i + 1]]
      if (min.y > y && min.x > x) min = {x, y}
    }
    const minX = min.x - this.marginX
    const minY = min.y - this.marginY
    return isToZero ? {x: x - minX, y: y - minY} : {x: x + minX, y: y + minY}
  }
  setCloneRotPosFromConfig (clone = this.currentObject) {
    const partName = this.getPartName(clone)
    const pInx = this.getIndex()
    const cInx = this.getIndex(clone.name) * 3
    const clones = this.platesConfig[pInx][partName] // rad
    const {x, y} = this.toPrinterZero(clones[cInx], clones[cInx + 1], false)
    const { RotationalAngle: [parentX, parentY, parentZ], scaling, unit } = this.partsConfig[partName] // deg
    const summZ = parentZ + this.rad2deg(clones[cInx + 2]) // deg
    clone.rotation.order = 'ZYX'
    clone.rotation.x = this.deg2rad(parentX)
    clone.rotation.y = this.deg2rad(parentY)
    clone.rotation.z = this.deg2rad(summZ)
    clone.position.x = x
    clone.position.y = y
    if (this.currentObject) {
      const guiConf = {RotationalAngle: [parentX, parentY, summZ], scaling, unit}
      this.refreshGuiData(guiConf)
    }
  }

  setCloneRotPosToConfig (clone = this.currentObject) {
    const partName = this.getPartName(clone)
    const pInx = this.getIndex()
    const cInx = this.getIndex(clone.name) * 3
    const {rotation: {z}, position} = clone // rad
    const {x, y} = this.toPrinterZero(position.x, position.y)
    const {RotationalAngle: [, , parentZ]} = this.partsConfig[partName] // deg
    const cloneConfig = this.platesConfig[pInx][partName]
    const cloneZ = this.rad2deg(z) - parentZ
    cloneConfig[cInx] = x
    cloneConfig[cInx + 1] = y
    cloneConfig[cInx + 2] = this.deg2rad(cloneZ)
  }

  saveAndRemoveCurrentObject () {
    if (!this.currentObject) return
    this.transformControl.detach(this.currentObject)
    this.currentObject = null
    this.transformControl.setMode('translate')
    this.transformControl.showY = true
    this.transformControl.showX = true
    this.transformControl.showZ = false
    this.refreshGuiData()
  }
  getPartName (clone = this.currentObject) {
    return clone.name.replace(/_copy.+/, '')
  }
  getPlateGroup (name = this.currentPlateName) {
    return this.scene.getObjectByName(name)
  }
  getIndex (name = this.currentPlateName) {
    return name.match(/_(\d)+$/)[1] - 1
  }

  onDocumentMouseClick (event) {
    this.contextMenuToggle(false)
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
    this.ray.setFromCamera(this.mouse, this.camera)
    if (this.isManualPlacementMode) {
      if (!this.params.orientation_mode) return
      const intersectsHandle = this.ray.intersectObjects([this.transformControl.children[0]], true)
      if (intersectsHandle.length) return
      const plateGroup = this.getPlateGroup()
      const intersects = this.ray.intersectObjects([plateGroup], true)
      if (!intersects.length) {
        return this.saveAndRemoveCurrentObject()
      }
      const newObj = intersects[0].object.parent
      if (this.currentObject) {
        if (this.currentObject.name === newObj.name) {
          const mode = this.transformControl.getMode()
          if (mode === 'translate') {
            this.transformControl.setMode('rotate')
            this.transformControl.showY = false
            this.transformControl.showX = false
            this.transformControl.showZ = true
          } else {
            this.transformControl.setMode('translate')
            this.transformControl.showY = true
            this.transformControl.showX = true
            this.transformControl.showZ = false
          }
        } else {
          this.saveAndRemoveCurrentObject()
          this.currentObject = newObj
          this.setCloneRotPosFromConfig()
          this.transformControl.attach(newObj)
        }
      } else {
        this.currentObject = newObj
        this.setCloneRotPosFromConfig()
        this.transformControl.attach(newObj)
      }
    } else {
      const intersects = this.ray.intersectObjects([this.mesh])
      if (!intersects.length) return
      if (this.params.brushMode) {
        this.mesh.updateMatrixWorld()
        var vert = new this.THREE.Vector3()
        var normal = new this.THREE.Vector3()
        for (var i = 0; i < this.mesh.geometry.attributes.position.count; i++) {
          vert = vert.set(
            this.mesh.geometry.attributes.position.array[i * 3],
            this.mesh.geometry.attributes.position.array[i * 3 + 1],
            this.mesh.geometry.attributes.position.array[i * 3 + 2]
          ).clone().applyMatrix4(this.mesh.matrixWorld)
          normal = normal.set(
            this.mesh.geometry.attributes.normal.array[i * 3],
            this.mesh.geometry.attributes.normal.array[i * 3 + 1],
            this.mesh.geometry.attributes.normal.array[i * 3 + 2]
          )
          var angle = normal.angleTo(intersects[0].face.normal)
          var dist = vert.distanceTo(intersects[0].point)
          if (dist < this.params.brushRadius) {
            if (!this.params.brushBothSide) {
              if (angle >= -Math.PI / 2 && angle <= Math.PI / 2) {
                this.faceSelect(Math.floor(i / 3))
              }
            } else {
              this.faceSelect(Math.floor(i / 3))
            }
          }
        }
        this.faceSelect(intersects[0].faceIndex / 3)
      } else {
        this.adjacentFaceSelect(intersects[0].faceIndex / 3, false)
      }
      this.highlightedFaces = []
    }
  }

  faceSelect (faceIndex) {
    const idxRestricted = this.restrictedFaces.indexOf(faceIndex)
    const idxAllowed = this.allowedFaces.indexOf(faceIndex)
    let isSelect = false
    if (this.facesMode === 'restrict') {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted === -1) {
        this.restrictedFaces.push(faceIndex)
        isSelect = true
      }
    } else if (this.facesMode === 'allow') {
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
      if (idxAllowed === -1) {
        this.allowedFaces.push(faceIndex)
        isSelect = true
      }
    } else {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
    }
    return isSelect
  }

  adjacentFaceSelect (faceIndex, highLight) {
    const curvatureClicked = this.curvaturePerFacet[faceIndex]
    let facetsAdjacent = [faceIndex]
    while (1) {
      const facetsAdjacentNew = []
      for (let iFacet = 0; iFacet < facetsAdjacent.length; iFacet++) {
        let isSelected
        if (highLight) {
          isSelected = this.checkHighlight(facetsAdjacent[iFacet])
        } else {
          isSelected = this.faceSelect(facetsAdjacent[iFacet])
        }
        if (isSelected) {
          for (let i = 0; i < 3; i++) {
            const curvature = this.curvaturePerFacet[this.facetAdjacents[3 * facetsAdjacent[iFacet] + i]]
            if (Math.abs(curvature - curvatureClicked) < this.maxCurvature * this.params.selectionTolerance / 100) {
              facetsAdjacentNew.push(this.facetAdjacents[3 * facetsAdjacent[iFacet] + i])
            }
          }
        }
      }
      if (facetsAdjacentNew.length === 0) {
        break
      } else {
        facetsAdjacent = facetsAdjacentNew
      }
    }
  }

  toggleFaceSelection (faceIndex) {
    let result = null
    const idxRestricted = this.restrictedFaces.indexOf(faceIndex)
    const idxAllowed = this.allowedFaces.indexOf(faceIndex)
    if (this.facesMode === 'restrict') {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
        result = false
      } else {
        this.restrictedFaces.push(faceIndex)
        result = true
      }
    } else if (this.facesMode === 'allow') {
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
        result = false
      } else {
        this.allowedFaces.push(faceIndex)
        result = true
      }
    } else {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
    }
    return result
  }

  onDocumentMouseMove (event) {
    if (this.isNested) return
    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
    this.ray.setFromCamera(this.mouse, this.camera)
    var intersects = this.ray.intersectObjects([this.mesh])
    if (intersects.length > 0) {
      var i, dist, angle
      var vert = new this.THREE.Vector3()
      var normal = new this.THREE.Vector3()
      if (event.shiftKey) {
        this.highlightedFaces = []
        if (this.params.brushMode) {
          this.mesh.updateMatrixWorld()
          for (i = 0; i < this.mesh.geometry.attributes.position.count; i++) {
            vert = vert.set(
              this.mesh.geometry.attributes.position.array[i * 3],
              this.mesh.geometry.attributes.position.array[i * 3 + 1],
              this.mesh.geometry.attributes.position.array[i * 3 + 2]
            ).clone().applyMatrix4(this.mesh.matrixWorld)
            normal = normal.set(
              this.mesh.geometry.attributes.normal.array[i * 3],
              this.mesh.geometry.attributes.normal.array[i * 3 + 1],
              this.mesh.geometry.attributes.normal.array[i * 3 + 2]
            )
            angle = normal.angleTo(intersects[0].face.normal)
            dist = vert.distanceTo(intersects[0].point)
            if (dist < this.params.brushRadius) {
              if (!this.params.brushBothSide) {
                if (angle >= -Math.PI / 2 && angle <= Math.PI / 2) {
                  this.faceSelect(Math.floor(i / 3))
                }
              } else {
                this.faceSelect(Math.floor(i / 3))
              }
            }
          }
          this.faceSelect(intersects[0].faceIndex / 3)
        } else {
          this.faceSelect(intersects[0].faceIndex / 3)
        }
      } else {
        this.highlightedFaces = []
        if (this.params.brushMode) {
          this.mesh.updateMatrixWorld()
          for (i = 0; i < this.mesh.geometry.attributes.position.count; i++) {
            vert = vert.set(
              this.mesh.geometry.attributes.position.array[i * 3],
              this.mesh.geometry.attributes.position.array[i * 3 + 1],
              this.mesh.geometry.attributes.position.array[i * 3 + 2]
            ).clone().applyMatrix4(this.mesh.matrixWorld)
            normal = normal.set(
              this.mesh.geometry.attributes.normal.array[i * 3],
              this.mesh.geometry.attributes.normal.array[i * 3 + 1],
              this.mesh.geometry.attributes.normal.array[i * 3 + 2]
            )
            angle = normal.angleTo(intersects[0].face.normal)
            dist = vert.distanceTo(intersects[0].point)
            if (dist < this.params.brushRadius) {
              if (!this.params.brushBothSide) {
                if (angle >= -Math.PI / 2 && angle < Math.PI / 2) {
                  this.checkHighlight(Math.floor(i / 3))
                }
              } else {
                this.checkHighlight(Math.floor(i / 3))
              }
            }
          }
          this.checkHighlight(intersects[0].faceIndex / 3)
        } else {
          this.highlightedFaces = []
          // this.adjacentFaceSelect(intersects[0].faceIndex / 3, true)
          this.checkHighlight(intersects[0].faceIndex / 3)
        }
      }
    } else {
      this.highlightedFaces = []
    }
  }

  onDocumentMouseUp () {
    if (this.isManualPlacementMode || !this.params.orientation_mode) return

    let group = this.groupPart
    if (this.isNested) {
      if (!this.transformControl.wasDragging) return
      group = this.scene.getObjectByName(this.currentPartName)
    }
    this.centeredPosition(group)
  }

  onDocumentContextMenu (event) {
    if (this.params.orientation_mode) {
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
      this.ray.setFromCamera(this.mouse, this.camera)
      var intersects = this.ray.intersectObjects([this.mesh])
      if (intersects.length > 0) {
        this.contextMenuToggle(true, {x: event.clientX, y: event.clientY})
        this.contextMenuIntersect = intersects[0].face.normal
      }
    }
  }
  roundRotation (group) {
    group.rotation.x = this.deg2rad(Math.round(this.rad2deg(group.rotation.x)))
    group.rotation.y = this.deg2rad(Math.round(this.rad2deg(group.rotation.y)))
    group.rotation.z = this.deg2rad(Math.round(this.rad2deg(group.rotation.z)))
  }
  updateConfig (data, config) {
    const {rotation, scaling, facesRestrictions, restriction, units} = data
    config.RotationalAngle = rotation
    config.scaling = scaling
    config.AllowedFaces = facesRestrictions.allowedFaces
    config.RestrictedFaces = facesRestrictions.restrictedFaces
    config.unit = units.part
    if (this.isNested && !this.allowZRotation && !this.isManualPlacement) {
      const [x, y] = rotation
      config.RotationalAngle = [x, y, 0]
    }
    if (units.hasOwnProperty('support')) {
      config.unit_supp = units.support
    }
    if (restriction) {
      config['AngleRestriction'] = restriction
    } else {
      config['AngleRestriction'] = []
    }
  }
  centeredPosition (group) {
    const bbox = new this.THREE.Box3().setFromObject(group)
    const modelBase = bbox.min.z
    const center = bbox.getCenter()
    if (!this.isManualPlacementMode) {
      group.position.x = group.position.x - center.x
      group.position.y = group.position.y - center.y
    }
    if (!this.uploadedSupports) {
      group.position.z = group.position.z - modelBase + this.params.hsub
    }
    this.roundRotation(group)
  }

  makeParallelToBuildPlate () {
    if (this.params.orientation_mode) {
      if (this.contextMenuIntersect) {
        const n = this.contextMenuIntersect.clone()
        const q = new this.THREE.Quaternion().setFromUnitVectors(n, new this.THREE.Vector3(0, 0, -1))
        this.groupPart.rotation.setFromQuaternion(q)
        this.centeredPosition(this.groupPart)
        this.contextMenuIntersect = null
      }
    }
  }

  checkHighlight (faceIndex) {
    const idxHighlighted = this.highlightedFaces.indexOf(faceIndex)
    if (idxHighlighted === -1) {
      this.highlightedFaces.push(faceIndex)
      return true
    }
    return false
  }

  colorChange (child, color) {
    if (!child.geometry.attributes.hasOwnProperty('color')) {
      let colorData = new Float32Array(child.geometry.attributes.position.count * 3)
      for (let i = 0; i < child.geometry.attributes.position.count; i++) {
        colorData[i * 3] = color.r
        colorData[i * 3 + 1] = color.g
        colorData[i * 3 + 2] = color.b
      }
      child.geometry.addAttribute('color', new this.THREE.BufferAttribute(colorData, 3))
    } else {
      for (let i = 0; i < child.geometry.attributes.color.count; i++) {
        child.geometry.attributes.color.array[i * 3] = color.r
        child.geometry.attributes.color.array[i * 3 + 1] = color.g
        child.geometry.attributes.color.array[i * 3 + 2] = color.b
      }
    }
    child.geometry.attributes.color.needsUpdate = true
  }

  colorFaces () {
    if (this.mesh) {
      for (let i = 0; i < this.mesh.geometry.attributes.color.count; i++) {
        this.mesh.geometry.attributes.color.array[i * 3] = this.colorObj.default.r
        this.mesh.geometry.attributes.color.array[i * 3 + 1] = this.colorObj.default.g
        this.mesh.geometry.attributes.color.array[i * 3 + 2] = this.colorObj.default.b
      }
      this.restrictedFaces.forEach((faceIndex) => {
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.restrict.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.restrict.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.restrict.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.restrict.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.restrict.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.restrict.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.restrict.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.restrict.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.restrict.b
      })
      this.allowedFaces.forEach((faceIndex) => {
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.allow.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.allow.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.allow.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.allow.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.allow.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.allow.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.allow.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.allow.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.allow.b
      })
      this.highlightedFaces.forEach((faceIndex) => {
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.highlighted.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.highlighted.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.highlighted.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.highlighted.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.highlighted.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.highlighted.b
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.highlighted.r
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.highlighted.g
        this.mesh.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.highlighted.b
      })
      this.mesh.geometry.attributes.color.needsUpdate = true
    }
  }

  drawGround () {
    var plane = new this.THREE.Mesh(
      new this.THREE.PlaneBufferGeometry(100000, 100000),
      new this.THREE.MeshPhongMaterial({ color: 0x73767b, specular: 0x000000 })
    )

    if (this.PRINTER_CONFIG_LIBRARY[this.PRINTER]) {
      plane.position.z = this.PRINTER_CONFIG_LIBRARY[this.PRINTER].zRotation
    } else if (this.PRINTERS_CONFIG[this.PRINTER]) {
      plane.position.z = (Number(this.PRINTERS_CONFIG[this.PRINTER].plateThickness) * -1) / 2
    } else {
      plane.position.z = (Number(this.CONFIG.plateThickness[0]) * -1) / 2
    }
    plane.receiveShadow = true
    this.groupBase.add(plane)
  }

  drawPrinter () {
    var THREE_TEXT = window['THREE_Text']
    const [x, y] = this.isManualPlacement ? [this.marginX, this.marginY] : [0, 0]
    const getPoints = (d, h, w = d) => {
      const halfD = d / 2
      const halfW = w / 2
      return [
        new this.THREE.Vector3(-halfD, -halfW, 0),
        new this.THREE.Vector3(-halfD, halfW, 0),
        new this.THREE.Vector3(halfD, halfW, 0),
        new this.THREE.Vector3(halfD, -halfW, 0),
        new this.THREE.Vector3(-halfD, -halfW, h),
        new this.THREE.Vector3(-halfD, halfW, h),
        new this.THREE.Vector3(halfD, halfW, h),
        new this.THREE.Vector3(halfD, -halfW, h)
      ]
    }
    const getMarginPoints = (d, h, w = d) => {
      const halfD = d / 2
      const halfW = w / 2
      return [
        new this.THREE.Vector3(-halfD + x, -halfW + y, 0),
        new this.THREE.Vector3(-halfD + x, halfW - y, 0),
        new this.THREE.Vector3(halfD - x, halfW - y, 0),
        new this.THREE.Vector3(halfD - x, -halfW + y, 0),
        new this.THREE.Vector3(-halfD + x, -halfW + y, h),
        new this.THREE.Vector3(-halfD + x, halfW - y, h),
        new this.THREE.Vector3(halfD - x, halfW - y, h),
        new this.THREE.Vector3(halfD - x, -halfW + y, h)
      ]
    }
    switch (this.PRINTER) {
      case 'EOSM100':
        var material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        var Diameter = 100
        var Height = 95
        var points = getPoints(Diameter, Height)
        var p1 = points[0]
        var p2 = points[1]
        var p3 = points[2]
        var p4 = points[3]
        var p5 = points[4]
        var p6 = points[5]
        var p7 = points[6]
        var p8 = points[7]
        var geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        var line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        var geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        var line2 = new this.THREE.Line(geometry2, material)
        var geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        var line3 = new this.THREE.Line(geometry3, material)
        var geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        var line4 = new this.THREE.Line(geometry4, material)
        var geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        var line5 = new this.THREE.Line(geometry5, material)
        var geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(p1, p2, p3, p4, p1)
        var line6 = new this.THREE.Line(geometry6, material)
        if (this.isManualPlacement) {
          var geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(Diameter, Height)
          var pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          var pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)
        // helper
        var grid = new this.THREE.GridHelper(100, 10, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        var arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 20, 0x000000)
        var arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 20, 0x000000)
        var arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        var arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        var arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        var Text2D = THREE_TEXT.Text2D
        var textAlign = THREE_TEXT.textAlign
        var sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
        sprdtext.position.set(54, 0, 17)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(Diameter / 2 - 5, Diameter / 2 + 10, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.05, 0.05, 0.05)
        // sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        // sprdtext.position.set(Diameter / 2 + 10, Diameter / 2, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-40, -55, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-57, -40, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-57, -57, 20)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('ϕ100 x 95mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -55, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = 0
        this.groupBase.position.y = 0
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      case 'ArcamQ20':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 350
        Height = 380
        points = getPoints(Diameter, Height)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(p1, p2, p3, p4, p1)
        line6 = new this.THREE.Line(geometry6, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.Geometry()
          geometry7.vertices = getMarginPoints(Diameter, Height)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)

        // helper
        grid = new this.THREE.GridHelper(350, 35, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        // arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 50, 0x000000)
        // arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 50, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        // this.scene.add(arrow1)
        // this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('x', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-130, -180, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-185, -150, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-185, -185, 50)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('ϕ350 x 380 mm', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -200, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -Diameter / 2
        this.groupBase.position.y = -Diameter / 2
        this.groupBase.position.z = -30
        this.scene.add(this.groupBase)
        break
      case 'EOSM290':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        points = getPoints(250, 325)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(250, 325)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
        sprdtext.position.set(144, 0, 17)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(105, 142, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        // sprdtext.position.set(140, 105, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 325mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -25
        this.scene.add(this.groupBase)
        break
      case 'EOSM400':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 400
        Height = 360
        points = getPoints(Diameter, Height)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(p1, p2, p3, p4, p1)
        line6 = new this.THREE.Line(geometry6, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(Diameter, Height)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)

        // helper
        grid = new this.THREE.GridHelper(400, 40, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(240, 0, 0), 50, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(205, 40, 0), 80, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        var arrow6 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(205, -40, 0), 80, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)
        this.scene.add(arrow6)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.25, 0.25, 0.25)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 219, 30)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(230, 0, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.2, 0.2, 0.2)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(260, 20, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-160, -205, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-215, -155, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-210, -210, 50)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('400 x 400 x 400 mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -210, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -200
        this.groupBase.position.y = -200
        this.groupBase.position.z = -40
        this.scene.add(this.groupBase)
        break
      case 'SLM280':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 280
        Height = 335
        points = getPoints(Diameter, Height)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(Diameter, Height)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(280, 28, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(145, 145, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(145, 20, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        arrow6 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(145, -20, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)
        this.scene.add(arrow6)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 159, 30)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(125, 156, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(158, 0, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-110, -145, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-150, -105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-150, -150, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('280 x 280 x 365mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -147, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -140
        this.groupBase.position.y = -140
        this.groupBase.position.z = -30
        this.scene.add(this.groupBase)
        break
      case 'RenishawAM250':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 250
        Height = 300
        points = getPoints(Diameter, Height)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(Diameter, Height)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 144, 22)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(105, 142, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(140, 105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 300mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      case 'RenishawAM500':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 250
        Height = 350
        points = getPoints(Diameter, Height)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(Diameter, Height)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 144, 22)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(105, 142, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(140, 105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 350mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      default:
        var length, width, height, thickness
        if (this.PRINTERS_CONFIG[this.PRINTER]) {
          const printerConfig = this.PRINTERS_CONFIG[this.PRINTER]
          length = printerConfig.plateX
          width = printerConfig.plateY
          height = printerConfig.plateZ
          thickness = printerConfig.plateThickness
        } else {
          length = this.CONFIG.PrinterXYZ[0]
          width = this.CONFIG.PrinterXYZ[1]
          height = this.CONFIG.PrinterXYZ[2]
          thickness = Number(this.CONFIG.plateThickness[0])
        }

        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        var buildPlate = new this.THREE.BoxBufferGeometry(length, width, thickness)
        buildPlate.computeFaceNormals()
        buildPlate.computeVertexNormals()
        buildPlate = new this.THREE.Mesh(buildPlate, material)

        // Recoater
        var recoaterShape = new this.THREE.Shape([
          new this.THREE.Vector2(0, 0),
          new this.THREE.Vector2(8, 4),
          new this.THREE.Vector2(8, 24),
          new this.THREE.Vector2(0, 24)
        ])
        var recoaterGeometry
        if (this.CONFIG.DirectionRecoater[0]) {
          recoaterGeometry = new this.THREE.ExtrudeGeometry(recoaterShape, {
            amount: width,
            bevelEnabled: false
          })
        } else {
          recoaterGeometry = new this.THREE.ExtrudeGeometry(recoaterShape, {
            amount: length,
            bevelEnabled: false
          })
        }
        var recoater = new this.THREE.Mesh(recoaterGeometry, material)
        if (this.CONFIG.DirectionRecoater[0]) {
          recoater.rotation.set(Math.PI / 2, 0, 0)
          recoater.position.set((length / 2) + 24, width / 2, 7)
        } else {
          recoater.rotation.set(Math.PI / 2, Math.PI / 2, 0)
          recoater.position.set(-length / 2, (width / 2) + 24, 7)
        }

        this.scene.add(recoater)

        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        points = getPoints(length, height, length)
        p1 = points[0]
        p2 = points[1]
        p3 = points[2]
        p4 = points[3]
        p5 = points[4]
        p6 = points[5]
        p7 = points[6]
        p8 = points[7]
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(p5, p6, p7, p8, p5)
        line1 = new this.THREE.Line(geometry1, material)
        line1.name = 'top'
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(p1, p5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(p2, p6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(p3, p7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(p4, p8)
        line5 = new this.THREE.Line(geometry5, material)
        if (this.isManualPlacement) {
          geometry7 = new this.THREE.BoxGeometry(1, 1, 1)
          geometry7.vertices = getMarginPoints(length, height, length)
          pMesh = new this.THREE.Mesh(geometry7, material)
          pMesh.position.z = 0.0002
          pBox = new this.THREE.BoxHelper(pMesh, 0xFF0000)
          pBox.name = 'printer'
          pBox.visible = false
          this.scene.add(pBox)
        }
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // Grid
        grid = new this.THREE.Geometry()
        material = new this.THREE.LineBasicMaterial({color: 0x555555})
        for (var i = 0; i <= length; i += 10) {
          grid.vertices.push(new this.THREE.Vector3(i, 0, 0))
          grid.vertices.push(new this.THREE.Vector3(i, width, 0))
        }
        grid.vertices.push(new this.THREE.Vector3(length, 0, 0))
        grid.vertices.push(new this.THREE.Vector3(length, width, 0))

        for (i = 0; i <= width; i += 10) {
          grid.vertices.push(new this.THREE.Vector3(0, i, 0))
          grid.vertices.push(new this.THREE.Vector3(length, i, 0))
        }
        grid.vertices.push(new this.THREE.Vector3(0, width, 0))
        grid.vertices.push(new this.THREE.Vector3(length, width, 0))

        var gridLine = new this.THREE.LineSegments(grid, material)
        gridLine.position.set(-length / 2, -width / 2, 0.0001)
        this.scene.add(gridLine)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3((length / 2) + 10, (width / 2) + 10, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3((length / 2) + 10, (width / 2) + 10, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        if (this.CONFIG.DirectionRecoater[0]) {
          sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
          sprdtext.position.set((length / 2) + 24 - 0.01, 0, 28)
        } else {
          sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
          sprdtext.position.set(0, (width / 2) + 24 - 0.01, 28)
        }

        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        if (this.CONFIG.DirectionRecoater[0]) {
          sprdtext.rotation.set(0, 0, 0)
          sprdtext.position.set((length / 2) - 8, (width / 2) + 22, 0)
        } else {
          sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
          sprdtext.position.set((length / 2) + 22, (width / 2) - 10, 0)
        }
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // if (this.CONFIG.DirectionRecoater[0]) {
        //   sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        //   sprdtext.position.set((length / 2) + 22, (width / 2) - 10, 0)
        // } else {
        //   sprdtext.rotation.set(0, 0, 0)
        //   sprdtext.position.set((length / 2) - 8, (width / 2) + 22, 0)
        // }
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-(length / 2) - 15 + 40, -(width / 2) - 10, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-(length / 2) - 15, -(width / 2) - 10 + 40, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-(length / 2) - 15, -(width / 2) - 10, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D(length + ' x ' + width + ' x ' + height + 'mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -(width / 2) - 24, 0)
        this.scene.add(sprdtext)

        this.groupBase.add(buildPlate)
        this.groupBase.position.z = -(thickness / 2)
        this.scene.add(this.groupBase)
        break
    }
  }

  setupInset () {
    var insetWidth = 150
    var insetHeight = 150
    // insetWidth = window.innerWidth
    // insetHeight = window.innerHeight
    this.container = document.getElementById('inset')
    this.container.width = insetWidth
    this.container.height = insetHeight

    // renderer
    this.renderer2 = new this.THREE.WebGLRenderer({alpha: true})
    this.renderer2.setClearColor(0x000000, 0)
    this.renderer2.setSize(insetWidth, insetHeight)
    this.container.appendChild(this.renderer2.domElement)

    // scene
    this.scene2 = new this.THREE.Scene()

    // camera
    this.camera2 = new this.THREE.PerspectiveCamera(50, insetWidth / insetHeight, 1, 1000)
    this.camera2.up = this.camera.up // important!

    // axis
    var axis2 = new this.THREE.AxisHelper(100)
    this.scene2.add(axis2)
  }

  togglePrintBoxVisible (isVisible = true) {
    const pBox = this.scene.getObjectByName('printer')
    pBox.visible = isVisible
  }

  getMinMaxVerticles (obj) {
    const vertices = obj.children[0].geometry.attributes.position.array
    let maxXMaxYMaxZ, maxXMaxYMinZ, maxXMinYMaxZ, maxXMinYMinZ, minXMaxYMaxZ, minXMaxYMinZ, minXMinYMaxZ, minXMinYMinZ
    const maxXMaxYQuart = []
    const maxXMinYQuart = []
    const minXMinYQuart = []
    const minXMaxYQuart = []
    for (let i = 0; i < vertices.length; i += 3) {
      const [x, y, z] = [vertices[i], vertices[i + 1], vertices[i + 2]]
      if (x > 0 && y > 0) maxXMaxYQuart.push(x, y, z)
      if (x > 0 && y < 0) maxXMinYQuart.push(x, y, z)
      if (x < 0 && y < 0) minXMinYQuart.push(x, y, z)
      if (x < 0 && y > 0) minXMaxYQuart.push(x, y, z)
    }
    for (let i = 0; i < maxXMaxYQuart.length; i += 3) {
      const [x, y, z] = [maxXMaxYQuart[i], maxXMaxYQuart[i + 1], maxXMaxYQuart[i + 2]]
      if (i === 0) maxXMaxYMaxZ = maxXMaxYMinZ = {x, y, z}
      if (maxXMaxYMaxZ.x < x && maxXMaxYMaxZ.y < y && maxXMaxYMaxZ.z < z) maxXMaxYMaxZ = {x, y, z}
      if (maxXMaxYMinZ.x < x && maxXMaxYMinZ.y < y && maxXMaxYMinZ.z > z) maxXMaxYMinZ = {x, y, z}
    }
    for (let i = 0; i < maxXMinYQuart.length; i += 3) {
      const [x, y, z] = [maxXMinYQuart[i], maxXMinYQuart[i + 1], maxXMinYQuart[i + 2]]
      if (i === 0) maxXMinYMaxZ = maxXMinYMinZ = {x, y, z}
      if (maxXMinYMaxZ.x < x && maxXMinYMaxZ.y > y && maxXMinYMaxZ.z < z) maxXMinYMaxZ = {x, y, z}
      if (maxXMinYMinZ.x < x && maxXMinYMinZ.y > y && maxXMinYMinZ.z > z) maxXMinYMinZ = {x, y, z}
    }
    for (let i = 0; i < minXMaxYQuart.length; i += 3) {
      const [x, y, z] = [minXMaxYQuart[i], minXMaxYQuart[i + 1], minXMaxYQuart[i + 2]]
      if (i === 0) minXMaxYMaxZ = minXMaxYMinZ = {x, y, z}
      if (minXMaxYMaxZ.x > x && minXMaxYMaxZ.y < y && minXMaxYMaxZ.z < z) minXMaxYMaxZ = {x, y, z}
      if (minXMaxYMinZ.x > x && minXMaxYMinZ.y < y && minXMaxYMinZ.z > z) minXMaxYMinZ = {x, y, z}
    }
    for (let i = 0; i < minXMinYQuart.length; i += 3) {
      const [x, y, z] = [minXMinYQuart[i], minXMinYQuart[i + 1], minXMinYQuart[i + 2]]
      if (i === 0) minXMinYMaxZ = minXMinYMinZ = {x, y, z}
      if (minXMinYMaxZ.x > x && minXMinYMaxZ.y > y && minXMinYMaxZ.z < z) minXMinYMaxZ = {x, y, z}
      if (minXMinYMinZ.x > x && minXMinYMinZ.y > y && minXMinYMinZ.z > z) minXMinYMinZ = {x, y, z}
    }
    obj.userData = [maxXMaxYMaxZ, maxXMaxYMinZ, maxXMinYMaxZ, maxXMinYMinZ, minXMaxYMaxZ, minXMaxYMinZ, minXMinYMaxZ, minXMinYMinZ]
  }

  isOutGround (vectors) {
    const ground = this.groupBase.children[1]
    ground.name = 'ground'
    const ray = new this.THREE.Raycaster()
    for (let vector of vectors) {
      ray.set(vector, new this.THREE.Vector3(0, 0, -1))
      const intersects = ray.intersectObjects([ground], true)
      if (!intersects.length) return true
    }
    return false
  }
  isHigherTop (vectors) {
    const top = this.scene.getObjectByName('top')
    const height = top.geometry.vertices[0].z
    for (let vector of vectors) {
      if (vector.z >= height) return true
    }
    return false
  }
  isOutOfMargin (vectors) {
    // if (!this.marginX && !this.marginY) return false
    const pBox = this.scene.getObjectByName('printer').geometry.attributes.position.array
    for (let {x, y} of vectors) {
      for (let i = 0; i < pBox.length; i += 3) {
        const [pX, pY] = [Math.abs(pBox[0]), Math.abs(pBox[1])]
        if (Math.abs(x) > pX || Math.abs(y) > pY) return true
      }
    }
    return false
  }

  checkOverlaps () {
    const plateGroup = this.getPlateGroup().children
    if (!plateGroup.length) return this.togglePrintBoxVisible(false)
    const pInx = this.getIndex()
    const config = this.platesConfig[pInx]
    let isOutGround = false
    let isHeigerThenPrinter = false
    let isOutOfMargin = false
    for (let clone of plateGroup) {
      let vectors = clone.userData
      const mtrx = clone.matrix
      vectors = vectors.map(({x, y, z}) => {
        const newVector = new this.THREE.Vector3(x, y, z)
        newVector.applyMatrix4(mtrx)
        return newVector
      })
      isHeigerThenPrinter = this.isHigherTop(vectors)
      if (isHeigerThenPrinter) break
      isOutOfMargin = this.isOutOfMargin(vectors)
      if (isOutOfMargin) break
      if (this.PRINTERS_CONFIG[this.PRINTER].plateMode) {
        isOutGround = this.isOutGround(vectors)
        if (isOutGround) break
      }
    }
    this.togglePrintBoxVisible(isOutGround || isHeigerThenPrinter || isOutOfMargin)
    config.errors = isOutGround || isHeigerThenPrinter || isOutOfMargin
  }

  onTranslateGroup () {
    if (!this.isManualPlacementMode || !this.currentObject) return
    const {x, y, z} = this.currentObject.position
    const pInx = this.getIndex()
    const cInx = this.getIndex(this.currentObject.name) * 3
    const partName = this.getPartName()
    let config = this.oldPosition[pInx][partName]
    let pX = config[cInx]
    let pY = config[cInx + 1]
    if (pX !== x || pY !== y) {
      const newConfig = []
      for (let i = 0; i < config.length; i += 3) {
        const [pX, pY, pZ] = [config[i], config[i + 1], config[i + 2]]
        if (i === cInx) {
          newConfig.push(x, y, z)
        } else newConfig.push(pX, pY, pZ)
      }
      this.oldPosition[pInx][partName] = newConfig
      this.setCloneRotPosToConfig()
      this.checkOverlaps()
    }
  }

  onRotateGroup (group, prev) {
    if (!this.params.supportStructures && !this.uploadedSupports && !this.isNested) {
      if (this.groupSupp.children.length > 0) {
        prev.supportTransparency = this.groupSupp.children[0].material.opacity
      }
      this.params.supportTransparency = prev.supportTransparency
      this._supports_transparency.updateDisplay()
    }
    if ((prev.scale !== group.scale.z || !this.params.orientation_mode) && !this.uploadedSupports) {
      prev.scale = group.scale.z
      this.params.scale_factor = group.scale.z / this.unit2mmConstant(this.CONFIG.unit)
      this._scale.updateDisplay()
    }
    if ((prev.x !== group.rotation.x || !this.params.orientation_mode) && !this.uploadedSupports) {
      prev.x = group.rotation.x
      this.params.Rotate_about_X = this.rad2deg(group.rotation.x)
      this._x.updateDisplay()
    }
    if ((prev.y !== group.rotation.y || !this.params.orientation_mode) && !this.uploadedSupports) {
      prev.y = group.rotation.y
      this.params.Rotate_about_Y = this.rad2deg(group.rotation.y)
      this._y.updateDisplay()
    }
    if ((prev.z !== group.rotation.z || !this.params.orientation_mode) && !this.uploadedSupports) {
      prev.z = group.rotation.z
      this.params.Rotate_about_Z = this.rad2deg(group.rotation.z)
      if (this.isNested && !this.allowZRotation && !this.isManualPlacement) return
      else this._z.updateDisplay()
    }
  }
  animate () {
    this.cancelID = requestAnimationFrame(this.animate.bind(this))

    this.controls.update()

    if (this.finishedLoading) {
      if (!this.isNested) {
        this.colorFaces()
      }
      if (this.camera.getWorldDirection().angleTo(new this.THREE.Vector3(0, 0, -1)) > Math.PI / 2) {
        this.groupBase.visible = false
      } else {
        this.groupBase.visible = true
      }
    }
    // copy position of the camera into inset
    this.camera2.position.copy(this.camera.position)
    this.camera2.position.sub(this.controls.target)
    this.camera2.position.setLength(300)
    this.camera2.lookAt(this.scene2.position)
    if (this.params.orientation_mode) {
      if (!this.isNested) {
        this.onRotateGroup(this.groupPart, this.old)
      } else if (!this.isManualPlacementMode) {
        this.toggleControlDisabled(this._z, false)
        const group = this['nestedPart' + this.currentPartName]
        const prev = this.old[this.currentPartName]
        this.onRotateGroup(group, prev)
      } else {
        if (this.currentObject) {
          this.toggleControlDisabled(this._z, false)
          this.onTranslateGroup()
          if (this.rad2deg(this.currentObject.rotation.z) !== this.params.Rotate_about_Z) {
            this.params.Rotate_about_Z = this.rad2deg(this.currentObject.rotation.z)
            this._z.updateDisplay()
            this.checkOverlaps()
            this.setCloneRotPosToConfig()
          }
          this.setTextGray(this._remove_part, false)
        } else {
          this.toggleControlDisabled(this._z, true)
          this.setTextGray(this._remove_part, true)
        }
      }
    }
    this.renderer.render(this.scene, this.camera)
    this.renderer2.render(this.scene2, this.camera2)
  }

  destroy () {
    cancelAnimationFrame(this.cancelID)
    this.renderer.domElement.addEventListener('dblclick', null, false)
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('click', this.onDocumentMouseClick)
    window.removeEventListener('mousemove', this.onDocumentMouseMove)
    window.removeEventListener('mouseup', this.onDocumentMouseUp, true)
    window.removeEventListener('contextmenu', this.onDocumentContextMenu)

    // Remove jquery created elements
    let elements = document.getElementsByClassName('ui-tooltip')
    while (elements.length > 0) {
      elements[0].parentNode.removeChild(elements[0])
    }

    this.scene = null
    this.projector = null
    this.camera = null
    this.controls = null
    this.transformControl = null
    function empty (elem) {
      while (elem.lastChild) elem.removeChild(elem.lastChild)
      elem.remove()
    }
    empty(this.renderer.domElement)
    empty(this.renderer2.domElement)
    empty(this.gui.domElement)
    this.renderer.dispose()
    this.renderer2.dispose()
    document.body.style.margin = '0'
    document.body.style.overflow = 'auto'
  }
}

export class OutputViewer {
  LIBRARY_DEPS = [
    '/static/js/three.js',
    '/static/js/controls/TrackballControls.js',
    '/static/js/Detector.js',
    '/static/js/libs/stats.min.js',
    '/static/js/loaders/STLLoader.js',
    '/static/js/math/Lut.js',
    '/static/js/libs/dat.gui.min.js',
    '/static/js/three-text2d.min.js'
  ]

  STYLESHEET_DEPS = []

  STATIC_PREFIX = '/static/models/stl/binary/'
  PRINTER_CONFIG_LIBRARY = {
    EOSM100: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM100.stl',
      position: [-100, -200, 200],
      zRotation: -15
    },
    EOSM290: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM290.stl',
      position: [-150, -350, 300],
      zRotation: -25
    },
    EOSM400: {
      base: this.STATIC_PREFIX + 'build_plate_EOSM400.stl',
      position: [-300, -500, 400],
      zRotation: -40
    },
    SLM280: {
      base: this.STATIC_PREFIX + 'build_plate_SLM280.stl',
      position: [-250, -350, 300],
      zRotation: -30
    },
    RenishawAM250: {
      base: this.STATIC_PREFIX + 'build_plate_RenishawAM250.stl',
      position: [-250, -350, 300],
      zRotation: -30
    },
    RenishawAM500: {
      base: this.STATIC_PREFIX + 'build_plate_RenishawAM500.stl',
      position: [-250, -350, 350],
      zRotation: -30
    },
    ArcamQ20: {
      base: this.STATIC_PREFIX + 'build_plate_arcamQ20.stl',
      position: [-350, -350, 200],
      zRotation: -15
    }
  }

  constructor (configs, files, callbacks) {
    this.PRINTER = configs.config.printer
    this.CONFIG = configs.config
    this.PRINTERS_CONFIG = configs.printersConfig
    this.STL_FILES = files.parts
    this.positionConfig = files.nestedPositionConfig
    this.nestedPartsNames = []
    this.isManualPlacement = configs.isManualPlacement
    this.platesConfig = []
    this.nestedPlateConfig = files.nestedPlateConfig ? files.nestedPlateConfig[0] : null
    this.nestedPositionWithRotation = configs.nestedPositionWithRotation
    this.suppConfig = files.support
    if (files.legend) {
      this.distortionConfig = files.legend
    } else {
      this.distortionConfig = files.displayConfig || files.displayConfigBinary
    }
    this.isPlate = true
    this.minMaxConfig = [{setMin: '', setMax: ''}]
    this.minMaxNestedConfig = {setMin: '', setMax: ''}
    this.currentInx = 0
    this.finishedLoadingCB = callbacks.finishedLoading
    this.getCurvaturePerFaceData = callbacks.getCurvaturePerFaceData
    this.curvatureLoading = callbacks.curvatureLoading
    this.switchBrushModeCallback = callbacks.switchBrushModeCallback
    this.STLList = []
    this.finishedLoading = false
    this.isNested = !!this.positionConfig
    this.isThermalPredict = !!this.distortionConfig
    this.isDistortionConfig = (this.isThermalPredict && (!!files.displayConfigBinary || !!this.distortionConfig[0].dispZ))
    this.isDistortionData = false
    this.isHasXYZDistortion = (this.isThermalPredict && this.STL_FILES.length === 3)
    this.isHasXYZNDistortion = (this.isThermalPredict && this.STL_FILES.length === 4)
    this.distortionAxis = 'z'
    this.distortionOptionSuffix = ''
    this.modifySupportsMode = false
    this.uploadedSupports = false
    this.isStrengthenSupp = false
    this.facesCount = []
    Promise.all([
      loadStylesheetList(this.STYLESHEET_DEPS),
      loadScriptsInOrder(this.LIBRARY_DEPS)
    ]).then(() => {
      callbacks.init && callbacks.init()
    })
  }

  preInitializer () {
    this.THREE = window['THREE']
    this.jQuery = window['$']
    this.support = new SupportOutput(this.THREE)
    this.dat = window['dat']
    var Detector = window['Detector']
    this.mouse = new this.THREE.Vector2()
    this.facesMode = 'erase'
    this.modifyMode = false
    this.restrictedFaces = this.CONFIG.RestrictedFaces ? this.CONFIG.RestrictedFaces.slice() : []
    this.allowedFaces = this.CONFIG.AllowedFaces ? this.CONFIG.AllowedFaces.slice() : []
    this.highlightedFaces = []
    this.curvaturePerFacet = []
    this.facetAdjacents = []
    this.maxCurvature = 0
    this.lutConfig = {
      colorMap: 'rainbow',
      numberOfColors: 512
    }

    this.INTERSECTED2 = null

    if (!Detector.webgl) Detector.addGetWebGLMessage()

    this.params = {
      critical_angle: this.CONFIG.critical_angle[0],
      Rotate_about_X: 0,
      Rotate_about_Y: 0,
      Rotate_about_Z: 0,
      brushMode: true,
      brushRadius: 1,
      brushBothSide: false,
      selectionTolerance: 1.5,
      showSupp: false,
      relocate: true,
      supportTransparency: 0.2,
      strengthenSuppTransparency: 0.7
    }

    this.color = {
      restrict: 0xff0000,
      allow: 0x00ff00,
      highlighted: 0xd5d5d5,
      default: 0x93969b,
      erase: 0x93969b
    }

    this.colorObj = {
      restrict: new this.THREE.Color(this.color.restrict),
      allow: new this.THREE.Color(this.color.allow),
      highlighted: new this.THREE.Color(this.color.highlighted),
      default: new this.THREE.Color(this.color.default),
      erase: new this.THREE.Color(this.color.erase)
    }

    document.body.style.margin = '0'
    document.body.style.overflow = 'hidden'
  }

  init () {
    this.showLegend = true
    this.preInitializer()
    this.scene = new this.THREE.Scene()
    this.scene.fog = new this.THREE.Fog(0xd4d4d4, 0.01, 4000)
    this.renderer = new this.THREE.WebGLRenderer({antialias: true, alpha: true})
    this.renderer.domElement.id = 'viewer3d'
    this.renderer.setClearColor(this.scene.fog.color)
    // renderer.setClearColor(0x000000, 0)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(window.innerWidth, window.innerHeight)
    document.body.appendChild(this.renderer.domElement)
    // camera
    this.camera = new this.THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 30, 10000)
    this.camera.up.set(0, 0, 1)
    if (this.PRINTER_CONFIG_LIBRARY[this.PRINTER]) {
      this.camera.position.set(...this.PRINTER_CONFIG_LIBRARY[this.PRINTER].position)
    } else if (this.PRINTERS_CONFIG[this.PRINTER]) {
      const printerConfig = this.PRINTERS_CONFIG[this.PRINTER]
      this.camera.position.set(
        printerConfig.plateX * -0.9,
        printerConfig.plateY * -1.2,
        printerConfig.plateZ,
      )
    } else {
      this.camera.position.set(
        this.CONFIG.PrinterXYZ[0] * -0.9,
        this.CONFIG.PrinterXYZ[1] * -1.2,
        this.CONFIG.PrinterXYZ[2]
      )
    }
    this.scene.add(this.camera)
    this.controls = new this.THREE.TrackballControls(this.camera, this.renderer.domElement)
    this.controls.rotateSpeed = 5.0
    this.controls.zoomSpeed = 5
    this.controls.panSpeed = 2
    this.controls.noZoom = false
    this.controls.noPan = false
    this.controls.staticMoving = true
    this.controls.dynamicDampingFactor = 0.3
    this.controls.enabled = true

    // Raycaster
    this.ray = new this.THREE.Raycaster()

    // light
    this.threePointLight()
    this.scene.add(new this.THREE.AmbientLight(0x333333))

    this.groupBase = new this.THREE.Group()
    this.drawGround()
    this.drawPrinter()
    this.groupPart = new this.THREE.Group() // part with z distortion
    this.groupSupp = new this.THREE.Group() // group of support structure
    let material = new this.THREE.MeshPhongMaterial({ color: 0xffffff, specular: 0x111111, shininess: 200, vertexColors: this.THREE.VertexColors })
    let materialSupp = () => new this.THREE.MeshPhongMaterial({ color: 0x8e44ad, side: this.THREE.DoubleSide })
    if (this.isNested) {
      this.STL_FILES.forEach((part, index) => {
        this.nestedPartsNames.push(part.match(/[A-Za-z0-9-._()]+\.stl/)[0])
        this['nestedPart' + this.nestedPartsNames[index]] = new this.THREE.Group()
        this['nestedSupp' + this.nestedPartsNames[index]] = new this.THREE.Group()
        this.STLList.push(this.STL_LOAD(this['nestedPart' + this.nestedPartsNames[index]], part, material, true))
      })
      // different quantity of supports and files. this.suppConfig contains unused polymerSupp.*_0m.stl
      const filteredSupports = this.suppConfig.filter(suppUrl => {
        return this.nestedPartsNames.some(partName => suppUrl.includes(partName.slice(0, -4)))
      })
      filteredSupports.forEach((support, index) => {
        this.STLList.push(this.STL_LOAD(this['nestedSupp' + this.nestedPartsNames[index]], support, materialSupp(), true))
      })
    } else {
      if (this.isDistortionConfig) {
        this.STLList.push(this.STL_LOAD(this.groupPart, this.STL_FILES[0], material, true))
      } else if (this.isHasXYZNDistortion) {
        this.groupPartX = new this.THREE.Group() // part with x distortion
        this.groupPartY = new this.THREE.Group() // part with y distortion
        this.groupPartZ = new this.THREE.Group() // part with z distortion
        this.groupPartN = new this.THREE.Group() // part with normal distortion
        this.groupPartNoColor = new this.THREE.Group() // part with no color
        this.STLList.push(this.STL_LOAD(this.groupPartN, this.STL_FILES[0], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartX, this.STL_FILES[1], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartY, this.STL_FILES[2], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartZ, this.STL_FILES[3], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartNoColor, this.STL_FILES[0], material, false))
        this.groupPart.add(this.groupPartNoColor)
      } else if (this.isHasXYZDistortion) {
        this.groupPartX = new this.THREE.Group() // part with x distortion
        this.groupPartY = new this.THREE.Group() // part with y distortion
        this.groupPartZ = new this.THREE.Group() // part with z distortion
        this.groupPartNoColor = new this.THREE.Group() // part with no color
        this.STLList.push(this.STL_LOAD(this.groupPartX, this.STL_FILES[0], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartY, this.STL_FILES[1], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartZ, this.STL_FILES[2], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartNoColor, this.STL_FILES[0], material, false))
        this.groupPart.add(this.groupPartNoColor)
      } else if (this.isThermalPredict) {
        this.groupPartZ = new this.THREE.Group() // part with z distortion
        this.groupPartNoColor = new this.THREE.Group() // part with no color
        this.STLList.push(this.STL_LOAD(this.groupPartZ, this.STL_FILES[0], material, false))
        this.STLList.push(this.STL_LOAD(this.groupPartNoColor, this.STL_FILES[0], material, false))
        if (this.suppConfig) {
          this.groupPart.add(this.groupPartNoColor)
        } else {
          this.groupPart.add(this.groupPartZ)
        }
      } else {
        this.groupPartNoColor = new this.THREE.Group() // part with no color
        this.STLList.push(this.STL_LOAD(this.groupPartNoColor, this.STL_FILES[0], material, false))
        this.groupPart.add(this.groupPartNoColor)
      }
      if (this.suppConfig) {
        this.suppConfig.forEach(support => {
          this.STLList.push(this.STL_LOAD(this.groupSupp, support, materialSupp(), true))
        })
      }
    }
    this.setupInset()
    Promise.all(this.STLList).then(() => {
      this.STL_LOAD_FINISHED()
    })
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onDocumentMouseClick = this.onDocumentMouseClick.bind(this)
    this.onDocumentMouseMove = this.onDocumentMouseMove.bind(this)
    this.colorFaces = this.colorFaces.bind(this)
    window.addEventListener('resize', this.onWindowResize)
    this.addEventListeners()
  }
  addEventListeners () {
    window.addEventListener('click', this.onDocumentMouseClick)
    window.addEventListener('mousemove', this.onDocumentMouseMove)
  }

  addSupport () {
    this.scene.add(this.groupSupp)
    // this.paintSupports()
  }

  removeSupport () {
    this.scene.remove(this.groupSupp)
  }

  changeSuppOpacity () {
    let opacity = this.isStrengthenSupp ? 0.2 : 0.7
    let strengthenOpacity = 0.7
    let meshGroupSupp = this.groupSupp.children
    if (this.modifySupportsMode) {
      opacity = this.params.supportTransparency
      strengthenOpacity = this.params.strengthenSuppTransparency
    }
    if (this.isNested) {
      meshGroupSupp = meshGroupSupp[0].children
    }
    const change = (group) => {
      const isMesh = group.type === 'Mesh'
      if (isMesh) {
        const isNameStrengthen = group.name === 'isStrengthenSupp'
        group.material.opacity = isNameStrengthen ? strengthenOpacity : opacity
        group.material.transparent = true
      } else {
        for (let child of group.children) {
          change(child)
        }
      }
    }
    for (let child of meshGroupSupp) {
      change(child)
    }
  }

  ShowSupp () {
    this.params.showSupp = true
    return new Promise((resolve) => {
      if (!this.modifySupportsMode) {
        this.addSupport()
        if (this.isDistortionConfig) {
          let part = 'groupPart'
          this.STL_FILES.forEach((file, index) => {
            if (this.isNested) {
              part = 'nestedPart' + this.nestedPartsNames[index]
            }
            this.changeColor(this[part], this.color.default)
          })
        } else if (this.isThermalPredict) {
          this.groupPart.remove(this.groupPart.children[0])
          this.groupPart.add(this.groupPartNoColor)
        }
        if (this.isThermalPredict) {
          this['cameraL'].layers.set(1)
        }
      }
      this.changeSuppOpacity()
      resolve()
    })
  }

  HideSupp () {
    this.params.showSupp = false
    if (this.modifySupportsMode) {
      this.changeSuppOpacity()
    } else {
      if (!(this.isDistortionConfig && this.distortionOptionSuffix === '_on') && this.isThermalPredict) {
        this.removeSupport()
      }
      if (this.isDistortionConfig) {
        this.switchDistortion()
        this.changeSuppOpacity()
      } else if (this.isThermalPredict) {
        this.groupPart.remove(this.groupPart.children[0])
        this.groupPart.add(this.groupPartZ)
      }
      if (this.isThermalPredict) {
        this['cameraL'].layers.set(0)
      } else {
        this.params.showSupp = true
      }
    }
  }

  paintSupports () {
    console.time('t1')
    const direction = new this.THREE.Vector3(0, 0, 1)
    for (let i = 0; i < this.groupSupp.children[0].geometry.vertices.length; i++) {
      this.ray.set(this.groupSupp.children[0].geometry.vertices[i].clone().applyMatrix4(this.groupPart.children[0].matrixWorld), direction)
      let intersects = this.ray.intersectObjects([this.groupPart.children[0]])
      if (intersects.length > 0) {
        this.groupSupp.children[0].geometry.faces[Math.floor(i / 3)].color.setHex(0x00ff00)
      }
    }
    console.timeEnd('t1')
    this.groupSupp.children[0].geometry.colorsNeedUpdate = true
  }

  getCurvaturePerFace () {
    this.curvatureLoading(true)
    this.getCurvaturePerFaceData()
      .then((data) => {
        this.curvaturePerFacet = data.curvature_per_facet
        this.facetAdjacents = data.triangles_adjacent_triangle
        for (let i = 0; i < this.curvaturePerFacet.length; i++) {
          if (this.curvaturePerFacet[i] > this.maxCurvature) {
            this.maxCurvature = this.curvaturePerFacet[i]
          }
        }
        this.curvatureLoading(false)
      })
      .catch((err) => {
        this.switchBrushModeCallback()
        this.curvatureLoading(false)
        console.log(err)
      })
  }

  changeColor (group, colorConfig) {
    let color
    if (typeof colorConfig === 'number') {
      color = new this.THREE.Color(colorConfig)
    }
    if (!group.children[0].geometry.attributes.hasOwnProperty('color')) {
      let colorData = new Float32Array(group.children[0].geometry.attributes.position.count * 3)
      for (let i = 0; i < group.children[0].geometry.attributes.position.count; i++) {
        colorData[i * 3] = color.r
        colorData[i * 3 + 1] = color.g
        colorData[i * 3 + 2] = color.b
      }
      group.children[0].geometry.addAttribute('color', new this.THREE.BufferAttribute(colorData, 3))
    } else {
      for (let i = 0; i < group.children[0].geometry.attributes.position.count / 3; i++) {
        if (colorConfig[0] && colorConfig[0].hasOwnProperty('r')) {
          group.children[0].geometry.attributes.color.array[i * 3 * 3] = colorConfig[i].r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 1] = colorConfig[i].g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 2] = colorConfig[i].b
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 3] = colorConfig[i].r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 4] = colorConfig[i].g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 5] = colorConfig[i].b
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 6] = colorConfig[i].r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 7] = colorConfig[i].g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 8] = colorConfig[i].b
        } else if (typeof colorConfig === 'number') {
          group.children[0].geometry.attributes.color.array[i * 3 * 3] = color.r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 1] = color.g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 2] = color.b
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 3] = color.r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 4] = color.g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 5] = color.b
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 6] = color.r
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 7] = color.g
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 8] = color.b
        } else {
          group.children[0].geometry.attributes.color.array[i * 3 * 3] = colorConfig[i * 3] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 1] = colorConfig[i * 3 + 1] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 2] = colorConfig[i * 3 + 2] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 3] = colorConfig[i * 3] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 4] = colorConfig[i * 3 + 1] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 5] = colorConfig[i * 3 + 2] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 6] = colorConfig[i * 3] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 7] = colorConfig[i * 3 + 1] / 255
          group.children[0].geometry.attributes.color.array[i * 3 * 3 + 8] = colorConfig[i * 3 + 2] / 255
        }
      }
    }
    group.children[0].geometry.attributes.color.needsUpdate = true
  }

  switchDistortionOption (option) {
    switch (option) {
      case 'default':
        this.distortionOptionSuffix = ''
        break
      case 'removal':
        this.distortionOptionSuffix = '_removal'
        break
      case 'on':
        this.distortionOptionSuffix = '_on'
        break
    }
    this.switchDistortion()
  }

  switchDistortion (axis = this.distortionAxis) {
    this.distortionAxis = axis
    if (this.isDistortionConfig) {
      this.STL_FILES.forEach((plate, index) => {
        let maxVal, minVal
        let part = 'groupPart'
        if (this.isNested) {
          part = 'nestedPart' + this.nestedPartsNames[index]
        }
        if (axis === 'n') {
          this.changeColor(this[part], this.distortionConfig[index]['dispN' + this.distortionOptionSuffix])
          minVal = this.distortionConfig[index]['dispLegendN' + this.distortionOptionSuffix][0]
          this.minMaxConfig[index].setMin = minVal
          maxVal = this.distortionConfig[index]['dispLegendN' + this.distortionOptionSuffix][1]
          this.minMaxConfig[index].setMax = maxVal
        } else if (axis === 'x') {
          this.changeColor(this[part], this.distortionConfig[index]['dispX' + this.distortionOptionSuffix])
          minVal = this.distortionConfig[index]['dispLegendX' + this.distortionOptionSuffix][0]
          this.minMaxConfig[index].setMin = minVal
          maxVal = this.distortionConfig[index]['dispLegendX' + this.distortionOptionSuffix][1]
          this.minMaxConfig[index].setMax = maxVal
        } else if (axis === 'y') {
          this.changeColor(this[part], this.distortionConfig[index]['dispY' + this.distortionOptionSuffix])
          minVal = this.distortionConfig[index]['dispLegendY' + this.distortionOptionSuffix][0]
          this.minMaxConfig[index].setMin = minVal
          maxVal = this.distortionConfig[index]['dispLegendY' + this.distortionOptionSuffix][1]
          this.minMaxConfig[index].setMax = maxVal
        } else if (axis === 'z') {
          this.changeColor(this[part], this.distortionConfig[index]['dispZ' + this.distortionOptionSuffix])
          minVal = this.distortionConfig[index]['dispLegendZ' + this.distortionOptionSuffix][0]
          this.minMaxConfig[index].setMin = minVal
          maxVal = this.distortionConfig[index]['dispLegendZ' + this.distortionOptionSuffix][1]
          this.minMaxConfig[index].setMax = maxVal
        } else {
          this.changeColor(this[part], this.color.default)
        }
      })
    } else if (this.isHasXYZDistortion) {
      this.groupPart.remove(this.groupPart.children[0])
      if (axis === 'x') {
        this.groupPart.add(this.groupPartX)
        this.minMaxConfig[0].setMin = this.distortionConfig[0].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[0].setMax[0]
      } else if (axis === 'y') {
        this.groupPart.add(this.groupPartY)
        this.minMaxConfig[0].setMin = this.distortionConfig[1].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[1].setMax[0]
      } else if (axis === 'z') {
        this.groupPart.add(this.groupPartZ)
        this.minMaxConfig[0].setMin = this.distortionConfig[2].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[2].setMax[0]
      } else {
        this.groupPart.add(this.groupPartNoColor)
      }
    } else if (this.isHasXYZNDistortion) {
      this.groupPart.remove(this.groupPart.children[0])
      if (axis === 'n') {
        this.groupPart.add(this.groupPartN)
        this.minMaxConfig[0].setMin = this.distortionConfig[0].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[0].setMax[0]
      } else if (axis === 'x') {
        this.groupPart.add(this.groupPartX)
        this.minMaxConfig[0].setMin = this.distortionConfig[1].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[1].setMax[0]
      } else if (axis === 'y') {
        this.groupPart.add(this.groupPartY)
        this.minMaxConfig[0].setMin = this.distortionConfig[2].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[2].setMax[0]
      } else if (axis === 'z') {
        this.groupPart.add(this.groupPartZ)
        this.minMaxConfig[0].setMin = this.distortionConfig[3].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[3].setMax[0]
      } else {
        this.groupPart.add(this.groupPartNoColor)
      }
    }
    if (this.isDistortionConfig || this.isHasXYZDistortion || this.isHasXYZNDistortion) {
      this.setNestedMinMax()
      this.selectLegendToAdd()
    }
  }

  switchModifySupportsMode (mode) {
    this.modifySupportsMode = mode
  }

  switchFacetSelectionMode (mode) {
    this.facesMode = mode
  }

  switchBrushMode (mode) {
    this.params.brushMode = mode
    if (!mode && this.curvaturePerFacet.length === 0) {
      this.getCurvaturePerFace()
    }
  }

  switchBrushSide (mode) {
    this.params.brushBothSide = mode
  }

  setBrushRadius (event) {
    this.params.brushRadius = event.target.value
  }

  setSelectionTolerance (event) {
    this.params.selectionTolerance = event.target.value
  }

  setSupportTransparency (event, isStrength) {
    const key = isStrength ? 'strengthenSuppTransparency' : 'supportTransparency'
    this.params[key] = event.target.value
    this.changeSuppOpacity()
  }
  setNestedMinMax () {
    let min = this.minMaxConfig[0].setMin
    let max = this.minMaxConfig[0].setMax
    for (let {setMin, setMax} of this.minMaxConfig) {
      if (min > setMin) min = setMin
      if (max < setMax) max = setMax
    }
    this.minMaxNestedConfig = {setMin: min, setMax: max}
  }

  initLegend () {
    if (this.isNested) {
      this.STL_FILES.forEach((part, index) => {
        this.minMaxConfig[index] = {
          setMin: this.distortionConfig[index].dispLegendZ_on[0],
          setMax: this.distortionConfig[index].dispLegendZ_on[1]
        }
      })
      this.setNestedMinMax()
    } else {
      if (this.isDistortionConfig) {
        if (this.distortionConfig[0].dispLegendZ_on) {
          this.minMaxConfig[0].setMin = this.distortionConfig[0].dispLegendZ_on[0]
          this.minMaxConfig[0].setMax = this.distortionConfig[0].dispLegendZ_on[1]
        } else {
          this.minMaxConfig[0].setMin = this.distortionConfig[0].dispLegendZ[0]
          this.minMaxConfig[0].setMax = this.distortionConfig[0].dispLegendZ[1]
        }
      } else if (this.isHasXYZNDistortion) {
        this.minMaxConfig[0].setMin = this.distortionConfig[3].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[3].setMax[0]
      } else if (this.isHasXYZDistortion) {
        this.minMaxConfig[0].setMin = this.distortionConfig[2].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[2].setMax[0]
      } else if (this.isThermalPredict) {
        this.minMaxConfig[0].setMin = this.distortionConfig[0].setMin[0]
        this.minMaxConfig[0].setMax = this.distortionConfig[0].setMax[0]
      }
    }
    if (!this.modifySupportsMode && this.isThermalPredict) {
      this.setupInsetLegend()
      this['cameraL'].layers.set(1)
    }
  }

  setDistColors (min, max) {
    this.STL_FILES.forEach((plate, index) => {
      let part = 'groupPart'
      if (this.isNested) {
        part = 'nestedPart' + this.nestedPartsNames[index]
      }
      let dist
      if (this.distortionAxis === 'n') {
        dist = this.distortionConfig[index]['distN' + this.distortionOptionSuffix]
      } else if (this.distortionAxis === 'x') {
        dist = this.distortionConfig[index]['distX' + this.distortionOptionSuffix]
      } else if (this.distortionAxis === 'y') {
        dist = this.distortionConfig[index]['distY' + this.distortionOptionSuffix]
      } else if (this.distortionAxis === 'z') {
        dist = this.distortionConfig[index]['distZ' + this.distortionOptionSuffix]
      }
      let dispConfigTemp = this.processColors(dist, min, max)
      this.changeColor(this[part], dispConfigTemp)
      if (this.isNested && this.isPlate) {
        this.minMaxNestedConfig.setMin = min
        this.minMaxNestedConfig.setMax = max
      } else {
        this.minMaxConfig[this.currentInx].setMin = min
        this.minMaxConfig[this.currentInx].setMax = max
      }
      if (this.INTERSECTED2) {
        const name = this.INTERSECTED2.parent.name || this.INTERSECTED2.parent.parent.name || ''
        if (!this.isNested || plate.match(new RegExp(name))) {
          const faceId = this.INTERSECTED2.faceID
          const {r, g, b} = dispConfigTemp[faceId]
          this.INTERSECTED2.faceColor = [r, g, b]
        }
      }
    })
  }
  setLegendFromElem () {
    if (this.isDistortionConfig) {
      let minVal = parseFloat(document.getElementById('legendLowerLimit').value)
      let maxVal = parseFloat(document.getElementById('legendUpperLimit').value)
      if (minVal >= maxVal) return
      this.setDistColors(minVal, maxVal)
    }
  }
  setLegendToElem (min, max) {
    let ele0 = document.getElementById('legendLowerLimit')
    let ele1 = document.getElementById('legendUpperLimit')
    if (ele0 && ele1) {
      ele0.value = Math.round(min * 10000) / 10000
      ele1.value = Math.round(max * 10000) / 10000
    }
    this.setDistColors(min, max)
  }

  applyNewLegendLimit () {
    this.setLegendFromElem()
    this.selectLegendToAdd()
  }

  selectLegendToAdd () {
    let min, max
    if (this.isNested && this.isPlate) {
      min = this.minMaxNestedConfig.setMin
      max = this.minMaxNestedConfig.setMax
    } else {
      min = this.minMaxConfig[this.currentInx].setMin
      max = this.minMaxConfig[this.currentInx].setMax
    }
    this.addLegend(min, max)
  }

  resetLegendLimit () {
    this.switchDistortion()
    this.setNestedMinMax()
    this.selectLegendToAdd()
  }

  parseBinFile (arrayBuffer, index) {
    const fileLength = arrayBuffer.byteLength
    const fullColorLength = (16 + this.facesCount[index] * 3) * 12
    const onColorLength = (16 + this.facesCount[index] * 3) * 4
    const fullDistLength = this.facesCount[index] * 12 * 4
    const onDistLength = this.facesCount[index] * 4 * 4
    const colorDataLength = this.facesCount[index] * 3
    const distDataLength = this.facesCount[index] * 4
    this.isDistortionData = fileLength === fullDistLength || fileLength === onDistLength
    const dispConfig = {
      dispLegendZ_on: [],
      dispZ_on: [],
      dispLegendY_on: [],
      dispY_on: [],
      dispLegendX_on: [],
      dispX_on: [],
      dispLegendN_on: [],
      dispN_on: []
    }

    if (fileLength === fullDistLength || fileLength === fullColorLength) {
      dispConfig.dispLegendZ = []
      dispConfig.dispZ = []
      dispConfig.dispLegendY = []
      dispConfig.dispY = []
      dispConfig.dispLegendX = []
      dispConfig.dispX = []
      dispConfig.dispLegendN = []
      dispConfig.dispN = []
      dispConfig.dispLegendZ_removal = []
      dispConfig.dispZ_removal = []
      dispConfig.dispLegendY_removal = []
      dispConfig.dispY_removal = []
      dispConfig.dispLegendX_removal = []
      dispConfig.dispX_removal = []
      dispConfig.dispLegendN_removal = []
      dispConfig.dispN_removal = []
    }

    let view
    if (this.isDistortionData) {
      if (fileLength === onDistLength) {
        dispConfig.distZ_on = new Float32Array(arrayBuffer, 0 * distDataLength, this.facesCount[index])
        dispConfig.distY_on = new Float32Array(arrayBuffer, 1 * distDataLength, this.facesCount[index])
        dispConfig.distX_on = new Float32Array(arrayBuffer, 2 * distDataLength, this.facesCount[index])
        dispConfig.distN_on = new Float32Array(arrayBuffer, 3 * distDataLength, this.facesCount[index])
        dispConfig.dispLegendZ_on = this.calculateMinMaxFixNaN(dispConfig.distZ_on)
        dispConfig.dispLegendY_on = this.calculateMinMaxFixNaN(dispConfig.distY_on)
        dispConfig.dispLegendX_on = this.calculateMinMaxFixNaN(dispConfig.distX_on)
        dispConfig.dispLegendN_on = this.calculateMinMaxFixNaN(dispConfig.distN_on)
        dispConfig.dispZ_on = this.processColors(dispConfig.distZ_on, dispConfig.dispLegendZ_on[0], dispConfig.dispLegendZ_on[1])
        dispConfig.dispY_on = this.processColors(dispConfig.distY_on, dispConfig.dispLegendY_on[0], dispConfig.dispLegendY_on[1])
        dispConfig.dispX_on = this.processColors(dispConfig.distX_on, dispConfig.dispLegendX_on[0], dispConfig.dispLegendX_on[1])
        dispConfig.dispN_on = this.processColors(dispConfig.distN_on, dispConfig.dispLegendN_on[0], dispConfig.dispLegendN_on[1])
      } else if (fileLength === fullDistLength) {
        dispConfig.distZ = new Float32Array(arrayBuffer, 0 * distDataLength, this.facesCount[index])
        dispConfig.distY = new Float32Array(arrayBuffer, 1 * distDataLength, this.facesCount[index])
        dispConfig.distX = new Float32Array(arrayBuffer, 2 * distDataLength, this.facesCount[index])
        dispConfig.distN = new Float32Array(arrayBuffer, 3 * distDataLength, this.facesCount[index])
        dispConfig.distZ_removal = new Float32Array(arrayBuffer, 4 * distDataLength, this.facesCount[index])
        dispConfig.distY_removal = new Float32Array(arrayBuffer, 5 * distDataLength, this.facesCount[index])
        dispConfig.distX_removal = new Float32Array(arrayBuffer, 6 * distDataLength, this.facesCount[index])
        dispConfig.distN_removal = new Float32Array(arrayBuffer, 7 * distDataLength, this.facesCount[index])
        dispConfig.distZ_on = new Float32Array(arrayBuffer, 8 * distDataLength, this.facesCount[index])
        dispConfig.distY_on = new Float32Array(arrayBuffer, 9 * distDataLength, this.facesCount[index])
        dispConfig.distX_on = new Float32Array(arrayBuffer, 10 * distDataLength, this.facesCount[index])
        dispConfig.distN_on = new Float32Array(arrayBuffer, 11 * distDataLength, this.facesCount[index])
        dispConfig.dispLegendZ = this.calculateMinMaxFixNaN(dispConfig.distZ)
        dispConfig.dispLegendY = this.calculateMinMaxFixNaN(dispConfig.distY)
        dispConfig.dispLegendX = this.calculateMinMaxFixNaN(dispConfig.distX)
        dispConfig.dispLegendN = this.calculateMinMaxFixNaN(dispConfig.distN)
        dispConfig.dispLegendZ_removal = this.calculateMinMaxFixNaN(dispConfig.distZ_removal)
        dispConfig.dispLegendY_removal = this.calculateMinMaxFixNaN(dispConfig.distY_removal)
        dispConfig.dispLegendX_removal = this.calculateMinMaxFixNaN(dispConfig.distX_removal)
        dispConfig.dispLegendN_removal = this.calculateMinMaxFixNaN(dispConfig.distN_removal)
        dispConfig.dispLegendZ_on = this.calculateMinMaxFixNaN(dispConfig.distZ_on)
        dispConfig.dispLegendY_on = this.calculateMinMaxFixNaN(dispConfig.distY_on)
        dispConfig.dispLegendX_on = this.calculateMinMaxFixNaN(dispConfig.distX_on)
        dispConfig.dispLegendN_on = this.calculateMinMaxFixNaN(dispConfig.distN_on)
        dispConfig.dispZ = this.processColors(dispConfig.distZ, dispConfig.dispLegendZ[0], dispConfig.dispLegendZ[1])
        dispConfig.dispY = this.processColors(dispConfig.distY, dispConfig.dispLegendY[0], dispConfig.dispLegendY[1])
        dispConfig.dispX = this.processColors(dispConfig.distX, dispConfig.dispLegendX[0], dispConfig.dispLegendX[1])
        dispConfig.dispN = this.processColors(dispConfig.distN, dispConfig.dispLegendN[0], dispConfig.dispLegendN[1])
        dispConfig.dispZ_removal = this.processColors(dispConfig.distZ_removal, dispConfig.dispLegendZ_removal[0], dispConfig.dispLegendZ_removal[1])
        dispConfig.dispY_removal = this.processColors(dispConfig.distY_removal, dispConfig.dispLegendY_removal[0], dispConfig.dispLegendY_removal[1])
        dispConfig.dispX_removal = this.processColors(dispConfig.distX_removal, dispConfig.dispLegendX_removal[0], dispConfig.dispLegendX_removal[1])
        dispConfig.dispN_removal = this.processColors(dispConfig.distN_removal, dispConfig.dispLegendN_removal[0], dispConfig.dispLegendN_removal[1])
        dispConfig.dispZ_on = this.processColors(dispConfig.distZ_on, dispConfig.dispLegendZ_on[0], dispConfig.dispLegendZ_on[1])
        dispConfig.dispY_on = this.processColors(dispConfig.distY_on, dispConfig.dispLegendY_on[0], dispConfig.dispLegendY_on[1])
        dispConfig.dispX_on = this.processColors(dispConfig.distX_on, dispConfig.dispLegendX_on[0], dispConfig.dispLegendX_on[1])
        dispConfig.dispN_on = this.processColors(dispConfig.distN_on, dispConfig.dispLegendN_on[0], dispConfig.dispLegendN_on[1])
      }
    } else {
      if (fileLength === onColorLength) {
        view = new DataView(arrayBuffer, 16 * 0 + colorDataLength * 0, 16)
        dispConfig.dispLegendZ_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 1 + colorDataLength * 1, 16)
        dispConfig.dispLegendY_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 2 + colorDataLength * 2, 16)
        dispConfig.dispLegendX_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 3 + colorDataLength * 3, 16)
        dispConfig.dispLegendN_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        dispConfig.dispZ_on = new Uint8Array(arrayBuffer, 16 * 1 + colorDataLength * 0, colorDataLength)
        dispConfig.dispY_on = new Uint8Array(arrayBuffer, 16 * 2 + colorDataLength * 1, colorDataLength)
        dispConfig.dispX_on = new Uint8Array(arrayBuffer, 16 * 3 + colorDataLength * 2, colorDataLength)
        dispConfig.dispN_on = new Uint8Array(arrayBuffer, 16 * 4 + colorDataLength * 3, colorDataLength)
      } else if (fileLength === fullColorLength) {
        view = new DataView(arrayBuffer, 16 * 0 + colorDataLength * 0, 16)
        dispConfig.dispLegendZ = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 1 + colorDataLength * 1, 16)
        dispConfig.dispLegendY = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 2 + colorDataLength * 2, 16)
        dispConfig.dispLegendX = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 3 + colorDataLength * 3, 16)
        dispConfig.dispLegendN = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 4 + colorDataLength * 4, 16)
        dispConfig.dispLegendZ_removal = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 5 + colorDataLength * 5, 16)
        dispConfig.dispLegendY_removal = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 6 + colorDataLength * 6, 16)
        dispConfig.dispLegendX_removal = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 7 + colorDataLength * 7, 16)
        dispConfig.dispLegendN_removal = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 8 + colorDataLength * 8, 16)
        dispConfig.dispLegendZ_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 9 + colorDataLength * 9, 16)
        dispConfig.dispLegendY_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 10 + colorDataLength * 10, 16)
        dispConfig.dispLegendX_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        view = new DataView(arrayBuffer, 16 * 11 + colorDataLength * 11, 16)
        dispConfig.dispLegendN_on = new Float64Array([view.getFloat64(0, true), view.getFloat64(8, true)])
        dispConfig.dispZ = new Uint8Array(arrayBuffer, 16 * 1 + colorDataLength * 0, colorDataLength)
        dispConfig.dispY = new Uint8Array(arrayBuffer, 16 * 2 + colorDataLength * 1, colorDataLength)
        dispConfig.dispX = new Uint8Array(arrayBuffer, 16 * 3 + colorDataLength * 2, colorDataLength)
        dispConfig.dispN = new Uint8Array(arrayBuffer, 16 * 4 + colorDataLength * 3, colorDataLength)
        dispConfig.dispZ_removal = new Uint8Array(arrayBuffer, 16 * 5 + colorDataLength * 4, colorDataLength)
        dispConfig.dispY_removal = new Uint8Array(arrayBuffer, 16 * 6 + colorDataLength * 5, colorDataLength)
        dispConfig.dispX_removal = new Uint8Array(arrayBuffer, 16 * 7 + colorDataLength * 6, colorDataLength)
        dispConfig.dispN_removal = new Uint8Array(arrayBuffer, 16 * 8 + colorDataLength * 7, colorDataLength)
        dispConfig.dispZ_on = new Uint8Array(arrayBuffer, 16 * 9 + colorDataLength * 8, colorDataLength)
        dispConfig.dispY_on = new Uint8Array(arrayBuffer, 16 * 10 + colorDataLength * 9, colorDataLength)
        dispConfig.dispX_on = new Uint8Array(arrayBuffer, 16 * 11 + colorDataLength * 10, colorDataLength)
        dispConfig.dispN_on = new Uint8Array(arrayBuffer, 16 * 12 + colorDataLength * 11, colorDataLength)
        // !!!   Using functions is MUCH SLOWER   !!!
        // dispConfig.dispLegendZ = this.getLegendValues(arrayBuffer, colorDataLength, 0)
        // dispConfig.dispLegendY = this.getLegendValues(arrayBuffer, colorDataLength, 1)
        // dispConfig.dispLegendX = this.getLegendValues(arrayBuffer, colorDataLength, 2)
        // dispConfig.dispLegendN = this.getLegendValues(arrayBuffer, colorDataLength, 3)
        // dispConfig.dispLegendZ_removal = this.getLegendValues(arrayBuffer, colorDataLength, 4)
        // dispConfig.dispLegendY_removal = this.getLegendValues(arrayBuffer, colorDataLength, 5)
        // dispConfig.dispLegendX_removal = this.getLegendValues(arrayBuffer, colorDataLength, 6)
        // dispConfig.dispLegendN_removal = this.getLegendValues(arrayBuffer, colorDataLength, 7)
        // dispConfig.dispLegendZ_on = this.getLegendValues(arrayBuffer, colorDataLength, 8)
        // dispConfig.dispLegendY_on = this.getLegendValues(arrayBuffer, colorDataLength, 9)
        // dispConfig.dispLegendX_on = this.getLegendValues(arrayBuffer, colorDataLength, 10)
        // dispConfig.dispLegendN_on = this.getLegendValues(arrayBuffer, colorDataLength, 11)
        // dispConfig.dispZ = this.getColorValues(arrayBuffer, colorDataLength, 0)
        // dispConfig.dispY = this.getColorValues(arrayBuffer, colorDataLength, 1)
        // dispConfig.dispX = this.getColorValues(arrayBuffer, colorDataLength, 2)
        // dispConfig.dispN = this.getColorValues(arrayBuffer, colorDataLength, 3)
        // dispConfig.dispZ_removal = this.getColorValues(arrayBuffer, colorDataLength, 4)
        // dispConfig.dispY_removal = this.getColorValues(arrayBuffer, colorDataLength, 5)
        // dispConfig.dispX_removal = this.getColorValues(arrayBuffer, colorDataLength, 6)
        // dispConfig.dispN_removal = this.getColorValues(arrayBuffer, colorDataLength, 7)
        // dispConfig.dispZ_on = this.getColorValues(arrayBuffer, colorDataLength, 8)
        // dispConfig.dispY_on = this.getColorValues(arrayBuffer, colorDataLength, 9)
        // dispConfig.dispX_on = this.getColorValues(arrayBuffer, colorDataLength, 10)
        // dispConfig.dispN_on = this.getColorValues(arrayBuffer, colorDataLength, 11)
      }
      Object.keys(dispConfig).forEach((config) => {
        if (config.match(/Legend/)) {
          dispConfig[config] = dispConfig[config].map((unit) => {
            return this.convertUnits(unit)
          })
        }
      })
    }
    return dispConfig
  }

  calculateMinMaxFixNaN (distData) {
    let min = Number.MAX_VALUE
    let max = Number.MIN_VALUE
    for (let i = 0; i < distData.length; i++) {
      if (isNaN(distData[i])) {
        distData[i] = 0
      }
      if (distData[i] < min) {
        min = distData[i]
      }
      if (distData[i] > max) {
        max = distData[i]
      }
    }
    return [min, max]
  }

  processColors (distData, distMin, distMax) {
    const colorData = []
    const lut = new this.THREE.Lut(this.lutConfig.colorMap, this.lutConfig.numberOfColors)
    lut.setMin(distMin)
    lut.setMax(distMax)
    for (let i = 0; i < distData.length; i++) {
      colorData[i] = lut.getColor(distData[i])
    }
    return colorData
  }

  convertUnits (unit) {
    return unit * 1000
  }

  getLegendValues (buffer, colorLength, offset) {
    const view = new DataView(buffer, 16 * offset + colorLength * offset, 16)
    return new Float64Array([view.getFloat64(0, true) * 1000, view.getFloat64(8, true) * 1000])
  }

  getColorValues (buffer, colorLength, offset) {
    return new Uint8Array(buffer, 16 * (offset + 1) + colorLength * offset, colorLength)
  }

  STL_LOAD (groupx, stlFile, material, isCalculateNormals) {
    return new Promise((resolve, reject) => {
      var loader = new this.THREE.STLLoader()
      loader.load(stlFile, (geometry) => {
        var mesh = new this.THREE.Mesh(geometry, material)
        var scalexyz = 1
        mesh.scale.set(scalexyz, scalexyz, scalexyz)
        mesh.castShadow = true
        mesh.receiveShadow = true
        groupx.add(mesh)
        if (isCalculateNormals) {
          const normals = groupx.children[0].geometry.attributes.normal.array
          let needCalc = true
          for (let normal of normals) {
            if (normal !== 0) {
              needCalc = false
              break
            }
          }
          if (needCalc) groupx.children[0].geometry.computeVertexNormals()
        }
        if (stlFile.match(/strengthen/)) {
          this.isStrengthenSupp = true
          mesh.name = 'isStrengthenSupp'
        }
        resolve(geometry)
      })
    })
  }

  STL_LOAD_FINISHED () {
    if (this.isNested) {
      this.createNestedPlates()
      this.STL_FILES.forEach((plate, index) => {
        // this['part' + index].children[0].geometry.computeFaceNormals()
        // this['part' + index].children[0].geometry.computeVertexNormals()
        this.facesCount[index] =
          this['nestedPart' + this.nestedPartsNames[index]].children[0].geometry.attributes.normal.count / 3
        this['nestedPart' + this.nestedPartsNames[index]].name = this.nestedPartsNames[index]
        if (!this.distortionConfig[index].hasOwnProperty('dispZ')) {
          this.distortionConfig[index] = this.parseBinFile(this.distortionConfig[index], index)
        }
        this.changeColor(this['nestedPart' + this.nestedPartsNames[index]], this.color.default)
      })
    } else {
      if (this.isDistortionConfig) {
        this.facesCount[0] = this.groupPart.children[0].geometry.attributes.normal.count / 3
        if (!this.distortionConfig[0].hasOwnProperty('dispZ')) {
          this.distortionConfig = this.distortionConfig.map((data, index) => {
            return this.parseBinFile(data, index)
          })
        }
        this.changeColor(this.groupPart, this.color.default)
        let bbox = new this.THREE.Box3().setFromObject(this.groupPart)
        let center = bbox.getCenter()
        this.groupPart.position.x = this.groupPart.position.x - center.x
        this.groupPart.position.y = this.groupPart.position.y - center.y
        this.groupSupp.position.x = this.groupSupp.position.x - center.x
        this.groupSupp.position.y = this.groupSupp.position.y - center.y
      } else {
        let bbox = new this.THREE.Box3().setFromObject(this.groupPart.children[0])
        let center = bbox.getCenter()
        this.groupSupp.position.x = this.groupSupp.position.x - center.x
        this.groupSupp.position.y = this.groupSupp.position.y - center.y
        if (this.groupPartX) {
          this.groupPartX.position.x = this.groupPartX.position.x - center.x
          this.groupPartX.position.y = this.groupPartX.position.y - center.y
        }
        if (this.groupPartY) {
          this.groupPartY.position.x = this.groupPartY.position.x - center.x
          this.groupPartY.position.y = this.groupPartY.position.y - center.y
        }
        if (this.groupPartZ) {
          this.groupPartZ.position.x = this.groupPartZ.position.x - center.x
          this.groupPartZ.position.y = this.groupPartZ.position.y - center.y
        }
        if (this.groupPartN) {
          this.groupPartN.position.x = this.groupPartN.position.x - center.x
          this.groupPartN.position.y = this.groupPartN.position.y - center.y
        }
        if (this.groupPartNoColor) {
          this.groupPartNoColor.position.x = this.groupPartNoColor.position.x - center.x
          this.groupPartNoColor.position.y = this.groupPartNoColor.position.y - center.y
          this.changeColor(this.groupPartNoColor, this.color.default)
        }
      }
    }
    this.initLegend()
    this.scene.add(this.groupPart)
    this.scene.add(this.groupSupp)
    const callbackData = {}
    if (this.isDistortionConfig) {
      callbackData.maxDistortion = this.distortionConfig[0].dispLegendZ_on[1]
      if (this.distortionConfig[0].dispLegendZ) {
        callbackData.maxDistortion = this.distortionConfig[0].dispLegendZ[1]
      }
      if (this.distortionConfig[0].dispLegendZ_on) {
        callbackData.isHasOptions = true
      }
      if (this.distortionConfig[0].dispLegendZ && this.distortionConfig[0].dispLegendZ_on) {
        callbackData.isHasOptionsFull = true
      }
    } else {
      this.params.showSupp = true
      this.changeSuppOpacity()
    }

    if (this.isNested) {
      this.distortionConfig.forEach((distortion) => {
        if (distortion.hasOwnProperty('dispLegendZ_on') && !distortion.hasOwnProperty('dispLegendZ')) {
          callbackData.isHasOptionsFull = false
        }
      })
      callbackData.legendsData = this.distortionConfig.map((distortion) => {
        return distortion.dispLegendZ_on
      })
    }
    this.finishedLoading = true
    this.finishedLoadingCB(callbackData)
    this.HideSupp()
  }

  createNestedPlates () {
    const offsetX = this.calculateOffset('x')
    const offsetY = this.calculateOffset('y')
    let bbox, center

    if (Object.keys(this.positionConfig).length) {
      Object.keys(this.positionConfig).forEach((key) => {
        this['nestedParts' + key] = new this.THREE.Group()
        this['nestedSupports' + key] = new this.THREE.Group()
        this.platesConfig.push(key)
        Object.keys(this.positionConfig[key]).forEach((partKey) => {
          const paramsByPart = this.nestedPositionWithRotation ? 3 : 2
          this['nestedPart' + partKey].children[0].geometry.computeBoundingBox()
          const partCenter = this['nestedPart' + partKey].children[0].geometry.boundingBox.getCenter()
          for (let i = 0; i < this.positionConfig[key][partKey].length / paramsByPart; i++) {
            this['nestedPart' + partKey + i] = this['nestedPart' + partKey].clone()
            this['nestedSupp' + partKey + i] = this['nestedSupp' + partKey].clone()
            this['nestedPart' + partKey + i].position.x = this.positionConfig[key][partKey][i * paramsByPart] + offsetX
            this['nestedPart' + partKey + i].position.y = this.positionConfig[key][partKey][i * paramsByPart + 1] + offsetY
            this['nestedSupp' + partKey + i].position.x = this.positionConfig[key][partKey][i * paramsByPart] + offsetX
            this['nestedSupp' + partKey + i].position.y = this.positionConfig[key][partKey][i * paramsByPart + 1] + offsetY
            if (this.nestedPositionWithRotation) {
              this['nestedPart' + partKey + i].children[0].position.x = this['nestedPart' + partKey + i].children[0].position.x - partCenter.x
              this['nestedPart' + partKey + i].children[0].position.y = this['nestedPart' + partKey + i].children[0].position.y - partCenter.y
              this['nestedSupp' + partKey + i].children[0].position.x = this['nestedSupp' + partKey + i].children[0].position.x - partCenter.x
              this['nestedSupp' + partKey + i].children[0].position.y = this['nestedSupp' + partKey + i].children[0].position.y - partCenter.y
              this['nestedPart' + partKey + i].rotateOnAxis(new this.THREE.Vector3(0, 0, 1), this.positionConfig[key][partKey][i * paramsByPart + 2] * (Math.PI / 180))
              this['nestedSupp' + partKey + i].rotateOnAxis(new this.THREE.Vector3(0, 0, 1), this.positionConfig[key][partKey][i * paramsByPart + 2] * (Math.PI / 180))
              this['nestedPart' + partKey + i].position.x = this['nestedPart' + partKey + i].position.x + partCenter.x
              this['nestedPart' + partKey + i].position.y = this['nestedPart' + partKey + i].position.y + partCenter.y
              this['nestedSupp' + partKey + i].position.x = this['nestedSupp' + partKey + i].position.x + partCenter.x
              this['nestedSupp' + partKey + i].position.y = this['nestedSupp' + partKey + i].position.y + partCenter.y
            }
            this['nestedPart' + partKey + i].name = partKey
            this['nestedParts' + key].add(this['nestedPart' + partKey + i])
            this['nestedSupports' + key].add(this['nestedSupp' + partKey + i])
          }
        })
        if (this.isManualPlacement) {
          const printerConfig = this.PRINTERS_CONFIG[this.PRINTER]
          const length = printerConfig.plateX
          const width = printerConfig.plateY
          center = {x: length / 2, y: width / 2}
        } else {
          bbox = new this.THREE.Box3().setFromObject(this['nestedParts' + key])
          center = bbox.getCenter()
        }
        this['nestedParts' + key].position.x = this['nestedParts' + key].position.x - center.x
        this['nestedParts' + key].position.y = this['nestedParts' + key].position.y - center.y
        this['nestedSupports' + key].position.x = this['nestedSupports' + key].position.x - center.x
        this['nestedSupports' + key].position.y = this['nestedSupports' + key].position.y - center.y
      })
      this.nestedPartsNames.forEach((partKey) => {
        this['nestedParts' + partKey] = new this.THREE.Group()
        this['nestedSupports' + partKey] = new this.THREE.Group()
        this['nestedParts' + partKey].name = partKey
        this['nestedPart' + partKey + 'single'] = this['nestedPart' + partKey].clone()
        this['nestedSupp' + partKey + 'single'] = this['nestedSupp' + partKey].clone()
        bbox = new this.THREE.Box3().setFromObject(this['nestedPart' + partKey + 'single'])
        center = bbox.getCenter()
        this['nestedPart' + partKey + 'single'].position.x = this['nestedPart' + partKey + 'single'].position.x - center.x
        this['nestedPart' + partKey + 'single'].position.y = this['nestedPart' + partKey + 'single'].position.y - center.y
        this['nestedSupp' + partKey + 'single'].position.x = this['nestedSupp' + partKey + 'single'].position.x - center.x
        this['nestedSupp' + partKey + 'single'].position.y = this['nestedSupp' + partKey + 'single'].position.y - center.y
        this['nestedParts' + partKey].add(this['nestedPart' + partKey + 'single'])
        this['nestedSupports' + partKey].add(this['nestedSupp' + partKey + 'single'])
      })
      this.groupPart.add(this['nestedPartsplate0'])
      this.groupSupp.add(this['nestedSupportsplate0'])
    }
  }

  switchNestedPlates (plate) {
    this.isPlate = !this.nestedPartsNames.includes(plate)
    this.nestedPartsNames.forEach((name, inx) => {
      if (name === plate) this.currentInx = inx
    })
    this.groupPart.remove(this.groupPart.children[0])
    this.groupSupp.remove(this.groupSupp.children[0])
    this.groupPart.add(this['nestedParts' + plate])
    this.groupSupp.add(this['nestedSupports' + plate])
    this.switchDistortion()
    if (this.params.showSupp) {
      this.ShowSupp()
    }
  }

  calculateOffset (axis) {
    if (this.nestedPlateConfig) {
      if (this.nestedPlateConfig.plateMode > 0) {
        return this.nestedPlateConfig.plateDiameter * -1 / 2
      } else {
        if (axis === 'x') {
          return this.nestedPlateConfig.plateWidth * -1 / 2
        } else if (axis === 'y') {
          return this.nestedPlateConfig.plateHeight * -1 / 2
        }
      }
    }
    return 0
  }

  onDocumentMouseClick (event) {
    if (this.modifyMode) {
      const part = this.getPartToColor()
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
      this.ray.setFromCamera(this.mouse, this.camera)
      var intersects = this.ray.intersectObjects([part])
      if (intersects.length > 0) {
        if (this.params.brushMode) {
          part.updateMatrixWorld()
          var vert = new this.THREE.Vector3()
          var normal = new this.THREE.Vector3()
          for (var i = 0; i < part.geometry.attributes.position.count; i++) {
            vert = vert.set(
              part.geometry.attributes.position.array[i * 3],
              part.geometry.attributes.position.array[i * 3 + 1],
              part.geometry.attributes.position.array[i * 3 + 2]
            ).clone().applyMatrix4(part.matrixWorld)
            normal = normal.set(
              part.geometry.attributes.normal.array[i * 3],
              part.geometry.attributes.normal.array[i * 3 + 1],
              part.geometry.attributes.normal.array[i * 3 + 2]
            )
            var angle = normal.angleTo(intersects[0].face.normal)
            var dist = vert.distanceTo(intersects[0].point)
            if (dist < this.params.brushRadius) {
              if (!this.params.brushBothSide) {
                if (angle >= -Math.PI / 2 && angle <= Math.PI / 2) {
                  this.faceSelect(Math.floor(i / 3))
                }
              } else {
                this.faceSelect(Math.floor(i / 3))
              }
            }
          }
          this.faceSelect(intersects[0].faceIndex / 3)
        } else {
          this.adjacentFaceSelect(intersects[0].faceIndex / 3, false)
        }
        this.highlightedFaces = []
      }
    } else if (!this.params.showSupp) {
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1

      this.ray.setFromCamera(this.mouse, this.camera)
      let intersects2 = this.ray.intersectObject(this.groupPart, true)

      if (intersects2.length > 0 && intersects2[ 0 ].object.geometry.attributes.hasOwnProperty('color')) {
        let faceID = intersects2[ 0 ].faceIndex / 3
        if (!this.INTERSECTED2 || this.INTERSECTED2 !== intersects2[ 0 ].object || this.INTERSECTED2.faceID !== faceID) {
          if (this.INTERSECTED2) {
            let faceID2 = this.INTERSECTED2.faceID
            let faceColor2 = this.INTERSECTED2.faceColor
            for (let iv = 0; iv < 3; iv++) {
              this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv] = faceColor2[0]
              this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv + 1] = faceColor2[1]
              this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv + 2] = faceColor2[2]
            }
            if (this.INTERSECTED2 !== intersects2[ 0 ].object) {
              this.INTERSECTED2.geometry.attributes.color.needsUpdate = true
            }
          }
          this.INTERSECTED2 = intersects2[0].object
          this.INTERSECTED2.faceID = faceID
          this.INTERSECTED2.faceColor = [
            intersects2[ 0 ].object.geometry.attributes.color.array[faceID * 9],
            intersects2[ 0 ].object.geometry.attributes.color.array[faceID * 9 + 1],
            intersects2[ 0 ].object.geometry.attributes.color.array[faceID * 9 + 2]
          ]
          for (let iv = 0; iv < 3; iv++) {
            this.INTERSECTED2.geometry.attributes.color.array[9 * faceID + 3 * iv] = 1
            this.INTERSECTED2.geometry.attributes.color.array[9 * faceID + 3 * iv + 1] = 1
            this.INTERSECTED2.geometry.attributes.color.array[9 * faceID + 3 * iv + 2] = 1
          }
          this.INTERSECTED2.geometry.attributes.color.needsUpdate = true
          let ele = document.getElementById('distortionAtSelect')
          let disVal
          let inx = 0
          if (this.isNested) {
            const name = intersects2[0].object.parent.name || intersects2[0].object.parent.parent.name
            this.nestedPartsNames.forEach((key, i) => {
              if (key === name) inx = i
            })
          }
          if (this.distortionOptionSuffix === '') {
            if (this.distortionAxis === 'n') {
              disVal = this.distortionConfig[inx].distN[faceID]
            } else if (this.distortionAxis === 'x') {
              disVal = this.distortionConfig[inx].distX[faceID]
            } else if (this.distortionAxis === 'y') {
              disVal = this.distortionConfig[inx].distY[faceID]
            } else if (this.distortionAxis === 'z') {
              disVal = this.distortionConfig[inx].distZ[faceID]
            }
          } else if (this.distortionOptionSuffix === '_removal') {
            if (this.distortionAxis === 'n') {
              disVal = this.distortionConfig[inx].distN_removal[faceID]
            } else if (this.distortionAxis === 'x') {
              disVal = this.distortionConfig[inx].distX_removal[faceID]
            } else if (this.distortionAxis === 'y') {
              disVal = this.distortionConfig[inx].distY_removal[faceID]
            } else if (this.distortionAxis === 'z') {
              disVal = this.distortionConfig[inx].distZ_removal[faceID]
            }
          } else if (this.distortionOptionSuffix === '_on') {
            if (this.distortionAxis === 'n') {
              disVal = this.distortionConfig[inx].distN_on[faceID]
            } else if (this.distortionAxis === 'x') {
              disVal = this.distortionConfig[inx].distX_on[faceID]
            } else if (this.distortionAxis === 'y') {
              disVal = this.distortionConfig[inx].distY_on[faceID]
            } else if (this.distortionAxis === 'z') {
              disVal = this.distortionConfig[inx].distZ_on[faceID]
            }
          }
          disVal = Math.round(disVal * 10000) / 10000
          if (ele) ele.innerHTML = `Distortion at select is ${disVal} mm`
        }
      }
      // else {
      //   if (this.INTERSECTED2) {
      //     let faceID2 = this.INTERSECTED2.faceID
      //     let faceColor2 = this.INTERSECTED2.faceColor
      //     for (let iv = 0; iv < 3; iv++) {
      //       this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv] = faceColor2[0]
      //       this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv + 1] = faceColor2[1]
      //       this.INTERSECTED2.geometry.attributes.color.array[9 * faceID2 + 3 * iv + 2] = faceColor2[2]
      //     }
      //     this.INTERSECTED2.geometry.attributes.color.needsUpdate = true
      //   }
      //   this.INTERSECTED2 = null
      // }
    }
  }

  faceSelect (faceIndex) {
    const idxRestricted = this.restrictedFaces.indexOf(faceIndex)
    const idxAllowed = this.allowedFaces.indexOf(faceIndex)
    let isSelect = false
    if (this.facesMode === 'restrict') {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted === -1) {
        this.restrictedFaces.push(faceIndex)
        isSelect = true
      }
    } else if (this.facesMode === 'allow') {
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
      if (idxAllowed === -1) {
        this.allowedFaces.push(faceIndex)
        isSelect = true
      }
    } else {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
    }
    return isSelect
  }

  adjacentFaceSelect (faceIndex, highLight) {
    const curvatureClicked = this.curvaturePerFacet[faceIndex]
    let facetsAdjacent = [faceIndex]
    while (1) {
      const facetsAdjacentNew = []
      for (let iFacet = 0; iFacet < facetsAdjacent.length; iFacet++) {
        let isSelected
        if (highLight) {
          isSelected = this.checkHighlight(facetsAdjacent[iFacet])
        } else {
          isSelected = this.faceSelect(facetsAdjacent[iFacet])
        }
        if (isSelected) {
          for (let i = 0; i < 3; i++) {
            const curvature = this.curvaturePerFacet[this.facetAdjacents[3 * facetsAdjacent[iFacet] + i]]
            if (Math.abs(curvature - curvatureClicked) < this.maxCurvature * this.params.selectionTolerance / 100) {
              facetsAdjacentNew.push(this.facetAdjacents[3 * facetsAdjacent[iFacet] + i])
            }
          }
        }
      }
      if (facetsAdjacentNew.length === 0) {
        break
      } else {
        facetsAdjacent = facetsAdjacentNew
      }
    }
  }

  toggleFaceSelection (faceIndex) {
    let result = null
    const idxRestricted = this.restrictedFaces.indexOf(faceIndex)
    const idxAllowed = this.allowedFaces.indexOf(faceIndex)
    if (this.facesMode === 'restrict') {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
        result = false
      } else {
        this.restrictedFaces.push(faceIndex)
        result = true
      }
    } else if (this.facesMode === 'allow') {
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
        result = false
      } else {
        this.allowedFaces.push(faceIndex)
        result = true
      }
    } else {
      if (idxAllowed > -1) {
        this.allowedFaces.splice(idxAllowed, 1)
      }
      if (idxRestricted > -1) {
        this.restrictedFaces.splice(idxRestricted, 1)
      }
    }
    return result
  }

  onDocumentMouseMove (event) {
    if (this.modifyMode) {
      const part = this.getPartToColor()
      this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
      this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
      this.ray.setFromCamera(this.mouse, this.camera)
      var intersects = this.ray.intersectObjects([part])
      if (intersects.length > 0) {
        var i, dist, angle
        var vert = new this.THREE.Vector3()
        var normal = new this.THREE.Vector3()
        if (event.shiftKey) {
          this.highlightedFaces = []
          if (this.params.brushMode) {
            part.updateMatrixWorld()
            for (i = 0; i < part.geometry.attributes.position.count; i++) {
              vert = vert.set(
                part.geometry.attributes.position.array[i * 3],
                part.geometry.attributes.position.array[i * 3 + 1],
                part.geometry.attributes.position.array[i * 3 + 2]
              ).clone().applyMatrix4(part.matrixWorld)
              normal = normal.set(
                part.geometry.attributes.normal.array[i * 3],
                part.geometry.attributes.normal.array[i * 3 + 1],
                part.geometry.attributes.normal.array[i * 3 + 2]
              )
              angle = normal.angleTo(intersects[0].face.normal)
              dist = vert.distanceTo(intersects[0].point)
              if (dist < this.params.brushRadius) {
                if (!this.params.brushBothSide) {
                  if (angle >= -Math.PI / 2 && angle <= Math.PI / 2) {
                    this.faceSelect(Math.floor(i / 3))
                  }
                } else {
                  this.faceSelect(Math.floor(i / 3))
                }
              }
            }
            this.faceSelect(intersects[0].faceIndex / 3)
          } else {
            this.faceSelect(intersects[0].faceIndex / 3)
          }
        } else {
          this.highlightedFaces = []
          if (this.params.brushMode) {
            part.updateMatrixWorld()
            for (i = 0; i < part.geometry.attributes.position.count; i++) {
              vert = vert.set(
                part.geometry.attributes.position.array[i * 3],
                part.geometry.attributes.position.array[i * 3 + 1],
                part.geometry.attributes.position.array[i * 3 + 2]
              ).clone().applyMatrix4(part.matrixWorld)
              normal = normal.set(
                part.geometry.attributes.normal.array[i * 3],
                part.geometry.attributes.normal.array[i * 3 + 1],
                part.geometry.attributes.normal.array[i * 3 + 2]
              )
              angle = normal.angleTo(intersects[0].face.normal)
              dist = vert.distanceTo(intersects[0].point)
              if (dist < this.params.brushRadius) {
                if (!this.params.brushBothSide) {
                  if (angle >= -Math.PI / 2 && angle < Math.PI / 2) {
                    this.checkHighlight(Math.floor(i / 3))
                  }
                } else {
                  this.checkHighlight(Math.floor(i / 3))
                }
              }
            }
            this.checkHighlight(intersects[0].faceIndex / 3)
          } else {
            this.highlightedFaces = []
            // this.adjacentFaceSelect(intersects[0].faceIndex / 3, true)
            this.checkHighlight(intersects[0].faceIndex / 3)
          }
        }
      } else {
        this.highlightedFaces = []
      }
    }
  }

  checkHighlight (faceIndex) {
    const idxHighlighted = this.highlightedFaces.indexOf(faceIndex)
    if (idxHighlighted === -1) {
      this.highlightedFaces.push(faceIndex)
      return true
    }
    return false
  }

  colorFaces () {
    const part = this.getPartToColor()
    if (this.modifyMode) {
      for (let i = 0; i < part.geometry.attributes.color.count; i++) {
        part.geometry.attributes.color.array[i * 3] = this.colorObj.default.r
        part.geometry.attributes.color.array[i * 3 + 1] = this.colorObj.default.g
        part.geometry.attributes.color.array[i * 3 + 2] = this.colorObj.default.b
      }
      this.restrictedFaces.forEach((faceIndex) => {
        part.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.restrict.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.restrict.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.restrict.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.restrict.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.restrict.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.restrict.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.restrict.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.restrict.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.restrict.b
      })
      this.allowedFaces.forEach((faceIndex) => {
        part.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.allow.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.allow.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.allow.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.allow.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.allow.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.allow.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.allow.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.allow.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.allow.b
      })
      this.highlightedFaces.forEach((faceIndex) => {
        part.geometry.attributes.color.array[faceIndex * 3 * 3] = this.colorObj.highlighted.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 1] = this.colorObj.highlighted.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 2] = this.colorObj.highlighted.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 3] = this.colorObj.highlighted.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 4] = this.colorObj.highlighted.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 5] = this.colorObj.highlighted.b
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 6] = this.colorObj.highlighted.r
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 7] = this.colorObj.highlighted.g
        part.geometry.attributes.color.array[faceIndex * 3 * 3 + 8] = this.colorObj.highlighted.b
      })
      part.geometry.attributes.color.needsUpdate = true
    } else {
      for (let i = 0; i < part.geometry.attributes.color.count; i++) {
        part.geometry.attributes.color.array[i * 3] = this.colorObj.default.r
        part.geometry.attributes.color.array[i * 3 + 1] = this.colorObj.default.g
        part.geometry.attributes.color.array[i * 3 + 2] = this.colorObj.default.b
      }
    }
  }

  getPartToColor () {
    let part
    if (this.groupPart.children[0] && this.groupPart.children[0].geometry) {
      part = this.groupPart.children[0]
    } else if (this.groupPart.children[0].children[0] && this.groupPart.children[0].children[0].geometry) {
      part = this.groupPart.children[0].children[0]
    }
    return part
  }

  getAllowedFaces () {
    return this.allowedFaces
  }

  getRestrictedFaces () {
    return this.restrictedFaces
  }

  resetFaces () {
    this.allowedFaces = []
    this.restrictedFaces = []
  }

  reset () {
    this.controls.reset()
  }

  light (color, {
    position, normalize = null, intensity = 0.5, castShadow = true
  }) {
    const light = new this.THREE.DirectionalLight(color)
    light.position.set(...position).normalize(normalize)
    light.intensity = intensity
    light.castShadow = castShadow
    return light
  }

  threePointLight () {
    const dist = 400
    this.scene.add(this.light(0xb8b8b8, { position: [1 * dist, 2 * dist, 1 * dist] }))
    this.scene.add(this.light(0xb8b8b8, { position: [-1 * dist, 0.6 * dist, 0.5 * dist], intensity: 2 }))
    this.scene.add(this.light(0xffffff, { position: [-0.3 * dist, -0.6 * dist, 0.8 * dist], normalize: 0xb8b8b8 }))
  }

  onWindowResize () {
    this.camera.aspect = window.innerWidth / window.innerHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(window.innerWidth, window.innerHeight)
  }

  drawGround () {
    var plane = new this.THREE.Mesh(
      new this.THREE.PlaneBufferGeometry(100000, 100000),
      new this.THREE.MeshPhongMaterial({ color: 0x73767b, specular: 0x000000 })
    )

    if (this.PRINTER_CONFIG_LIBRARY[this.PRINTER]) {
      plane.position.z = this.PRINTER_CONFIG_LIBRARY[this.PRINTER].zRotation
    } else if (this.PRINTERS_CONFIG[this.PRINTER]) {
      plane.position.z = (Number(this.PRINTERS_CONFIG[this.PRINTER].plateThickness) * -1) / 2
    } else {
      plane.position.z = (Number(this.CONFIG.plateThickness[0]) * -1) / 2
    }
    plane.receiveShadow = true
    this.groupBase.add(plane)
  }

  drawPrinter () {
    var THREE_TEXT = window['THREE_Text']
    switch (this.PRINTER) {
      case 'EOSM100':
        var material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        var Diameter = 100
        var Height = 95
        var point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        var point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        var point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        var point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        var point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        var point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        var point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        var point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        var geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        var line1 = new this.THREE.Line(geometry1, material)
        var geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        var line2 = new this.THREE.Line(geometry2, material)
        var geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        var line3 = new this.THREE.Line(geometry3, material)
        var geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        var line4 = new this.THREE.Line(geometry4, material)
        var geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        var line5 = new this.THREE.Line(geometry5, material)
        var geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(point1, point2, point3, point4, point1)
        var line6 = new this.THREE.Line(geometry6, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)

        // helper
        var grid = new this.THREE.GridHelper(100, 10, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        var arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 20, 0x000000)
        var arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 20, 0x000000)
        var arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        var arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        var arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 20, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        var Text2D = THREE_TEXT.Text2D
        var textAlign = THREE_TEXT.textAlign
        var sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
        sprdtext.position.set(54, 0, 17)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(Diameter / 2 - 5, Diameter / 2 + 10, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.05, 0.05, 0.05)
        // sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        // sprdtext.position.set(Diameter / 2 + 10, Diameter / 2, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-40, -55, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-57, -40, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-57, -57, 20)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('ϕ100 x 95mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -55, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = 0
        this.groupBase.position.y = 0
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      case 'ArcamQ20':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 350
        Height = 380
        point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(point1, point2, point3, point4, point1)
        line6 = new this.THREE.Line(geometry6, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)

        // helper
        grid = new this.THREE.GridHelper(350, 35, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        // arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 50, 0x000000)
        // arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(Diameter / 2 + 5, Diameter / 2 + 5, 0), 50, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        // this.scene.add(arrow1)
        // this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('x', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-130, -180, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-185, -150, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.05, 0.05, 0.05)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-185, -185, 50)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('ϕ350 x 380 mm', { align: textAlign.center, font: '150px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -200, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -Diameter / 2
        this.groupBase.position.y = -Diameter / 2
        this.groupBase.position.z = -30
        this.scene.add(this.groupBase)
        break
      case 'EOSM290':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        point1 = new this.THREE.Vector3(-125, -125, 0)
        point2 = new this.THREE.Vector3(-125, 125, 0)
        point3 = new this.THREE.Vector3(125, 125, 0)
        point4 = new this.THREE.Vector3(125, -125, 0)
        point5 = new this.THREE.Vector3(-125, -125, 325)
        point6 = new this.THREE.Vector3(-125, 125, 325)
        point7 = new this.THREE.Vector3(125, 125, 325)
        point8 = new this.THREE.Vector3(125, -125, 325)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
        sprdtext.position.set(144, 0, 17)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(105, 142, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        // sprdtext.position.set(140, 105, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 325mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -25
        this.scene.add(this.groupBase)
        break
      case 'EOSM400':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 400
        Height = 360
        point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        geometry6 = new this.THREE.Geometry()
        geometry6.vertices.push(point1, point2, point3, point4, point1)
        line6 = new this.THREE.Line(geometry6, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)
        this.scene.add(line6)

        // helper
        grid = new this.THREE.GridHelper(400, 40, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(240, 0, 0), 50, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(205, 40, 0), 80, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(Diameter / 2 + 5), -(Diameter / 2 + 5), 0), 50, 0x000000)
        arrow6 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(205, -40, 0), 80, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)
        this.scene.add(arrow6)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.25, 0.25, 0.25)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 219, 30)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(230, 0, 0)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.2, 0.2, 0.2)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(260, 20, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-160, -205, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-215, -155, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-210, -210, 50)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('400 x 400 x 400 mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.2, 0.2, 0.2)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -210, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -200
        this.groupBase.position.y = -200
        this.groupBase.position.z = -40
        this.scene.add(this.groupBase)
        break
      case 'SLM280':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 280
        Height = 335
        point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(280, 28, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(145, 145, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(145, 20, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-145, -145, 0), 40, 0x000000)
        var arrow6 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(145, -20, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)
        this.scene.add(arrow6)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 159, 30)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(125, 156, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(158, 0, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-110, -145, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-150, -105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-150, -150, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('280 x 280 x 365mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -147, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -140
        this.groupBase.position.y = -140
        this.groupBase.position.z = -30
        this.scene.add(this.groupBase)
        break
      case 'RenishawAM250':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 250
        Height = 300
        point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 144, 22)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(105, 142, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(140, 105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 300mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      case 'RenishawAM500':
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        this.STLList.push(this.STL_LOAD(this.groupBase, this.PRINTER_CONFIG_LIBRARY[this.PRINTER].base, material, false))
        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        Diameter = 250
        Height = 350
        point1 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, 0)
        point2 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, 0)
        point3 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, 0)
        point4 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, 0)
        point5 = new this.THREE.Vector3(-Diameter / 2, -Diameter / 2, Height)
        point6 = new this.THREE.Vector3(-Diameter / 2, Diameter / 2, Height)
        point7 = new this.THREE.Vector3(Diameter / 2, Diameter / 2, Height)
        point8 = new this.THREE.Vector3(Diameter / 2, -Diameter / 2, Height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // helper
        grid = new this.THREE.GridHelper(250, 25, 0xffffff, 0x555555)
        grid.rotateOnAxis(new this.THREE.Vector3(1, 0, 0), 90 * (Math.PI / 180))
        grid.position.set(0, 0, 0.0001)
        this.scene.add(grid)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3(130, 130, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-130, -130, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(0, 144, 22)
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // sprdtext.rotation.set(0, 0, 0)
        // sprdtext.position.set(105, 142, 0)
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        sprdtext.position.set(140, 105, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-95, -130, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-135, -90, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-135, -135, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('250 x 250 x 350mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -130, 0)
        this.scene.add(sprdtext)

        this.groupBase.position.x = -125
        this.groupBase.position.y = -125
        this.groupBase.position.z = -15
        this.scene.add(this.groupBase)
        break
      default:
        var length, width, height, thickness
        if (this.PRINTERS_CONFIG[this.PRINTER]) {
          const printerConfig = this.PRINTERS_CONFIG[this.PRINTER]
          length = printerConfig.plateX
          width = printerConfig.plateY
          height = printerConfig.plateZ
          thickness = printerConfig.plateThickness
        } else {
          length = this.CONFIG.PrinterXYZ[0]
          width = this.CONFIG.PrinterXYZ[1]
          height = this.CONFIG.PrinterXYZ[2]
          thickness = Number(this.CONFIG.plateThickness[0])
        }
        material = new this.THREE.MeshPhongMaterial({ color: 0xAAAAAA, specular: 0x111111, shininess: 200 })
        var buildPlate = new this.THREE.BoxBufferGeometry(length, width, thickness)
        buildPlate.computeVertexNormals()
        buildPlate = new this.THREE.Mesh(buildPlate, material)

        // Recoater
        var recoaterShape = new this.THREE.Shape([
          new this.THREE.Vector2(0, 0),
          new this.THREE.Vector2(8, 4),
          new this.THREE.Vector2(8, 24),
          new this.THREE.Vector2(0, 24)
        ])
        var recoaterGeometry
        if (this.CONFIG.DirectionRecoater[0]) {
          recoaterGeometry = new this.THREE.ExtrudeGeometry(recoaterShape, {
            amount: width,
            bevelEnabled: false
          })
        } else {
          recoaterGeometry = new this.THREE.ExtrudeGeometry(recoaterShape, {
            amount: length,
            bevelEnabled: false
          })
        }
        var recoater = new this.THREE.Mesh(recoaterGeometry, material)
        if (this.CONFIG.DirectionRecoater[0]) {
          recoater.rotation.set(Math.PI / 2, 0, 0)
          recoater.position.set((length / 2) + 24, width / 2, 7)
        } else {
          recoater.rotation.set(Math.PI / 2, Math.PI / 2, 0)
          recoater.position.set(-length / 2, (width / 2) + 24, 7)
        }

        this.scene.add(recoater)

        material = new this.THREE.LineBasicMaterial({color: 0x000000})
        point1 = new this.THREE.Vector3(-length / 2, -width / 2, 0)
        point2 = new this.THREE.Vector3(-length / 2, width / 2, 0)
        point3 = new this.THREE.Vector3(length / 2, width / 2, 0)
        point4 = new this.THREE.Vector3(length / 2, -width / 2, 0)
        point5 = new this.THREE.Vector3(-length / 2, -width / 2, height)
        point6 = new this.THREE.Vector3(-length / 2, width / 2, height)
        point7 = new this.THREE.Vector3(length / 2, width / 2, height)
        point8 = new this.THREE.Vector3(length / 2, -width / 2, height)
        geometry1 = new this.THREE.Geometry()
        geometry1.vertices.push(point5, point6, point7, point8, point5)
        line1 = new this.THREE.Line(geometry1, material)
        geometry2 = new this.THREE.Geometry()
        geometry2.vertices.push(point1, point5)
        line2 = new this.THREE.Line(geometry2, material)
        geometry3 = new this.THREE.Geometry()
        geometry3.vertices.push(point2, point6)
        line3 = new this.THREE.Line(geometry3, material)
        geometry4 = new this.THREE.Geometry()
        geometry4.vertices.push(point3, point7)
        line4 = new this.THREE.Line(geometry4, material)
        geometry5 = new this.THREE.Geometry()
        geometry5.vertices.push(point4, point8)
        line5 = new this.THREE.Line(geometry5, material)
        this.scene.add(line1)
        this.scene.add(line2)
        this.scene.add(line3)
        this.scene.add(line4)
        this.scene.add(line5)

        // Grid
        grid = new this.THREE.Geometry()
        material = new this.THREE.LineBasicMaterial({color: 0x555555})
        for (var i = 0; i <= length; i += 10) {
          grid.vertices.push(new this.THREE.Vector3(i, 0, 0))
          grid.vertices.push(new this.THREE.Vector3(i, width, 0))
        }
        grid.vertices.push(new this.THREE.Vector3(length, 0, 0))
        grid.vertices.push(new this.THREE.Vector3(length, width, 0))

        for (i = 0; i <= width; i += 10) {
          grid.vertices.push(new this.THREE.Vector3(0, i, 0))
          grid.vertices.push(new this.THREE.Vector3(length, i, 0))
        }
        grid.vertices.push(new this.THREE.Vector3(0, width, 0))
        grid.vertices.push(new this.THREE.Vector3(length, width, 0))

        var gridLine = new this.THREE.LineSegments(grid, material)
        gridLine.position.set(-length / 2, -width / 2, 0.0001)
        this.scene.add(gridLine)

        arrow1 = new this.THREE.ArrowHelper(new this.THREE.Vector3(-1, 0, 0), new this.THREE.Vector3((length / 2) + 10, (width / 2) + 10, 0), 40, 0x000000)
        arrow2 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, -1, 0), new this.THREE.Vector3((length / 2) + 10, (width / 2) + 10, 0), 40, 0x000000)
        arrow3 = new this.THREE.ArrowHelper(new this.THREE.Vector3(1, 0, 0), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        arrow4 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 1, 0), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        arrow5 = new this.THREE.ArrowHelper(new this.THREE.Vector3(0, 0, 1), new this.THREE.Vector3(-(length / 2) - 10, -(width / 2) - 10, 0), 40, 0x000000)
        this.scene.add(arrow1)
        this.scene.add(arrow2)
        this.scene.add(arrow3)
        this.scene.add(arrow4)
        this.scene.add(arrow5)

        Text2D = THREE_TEXT.Text2D
        textAlign = THREE_TEXT.textAlign
        sprdtext = new Text2D('Recoater', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        if (this.CONFIG.DirectionRecoater[0]) {
          sprdtext.rotation.set(0.5 * Math.PI, -0.5 * Math.PI, 0)
          sprdtext.position.set((length / 2) + 24 - 0.01, 0, 28)
        } else {
          sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
          sprdtext.position.set(0, (width / 2) + 24 - 0.01, 28)
        }

        this.scene.add(sprdtext)
        sprdtext = new Text2D('recoating', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        if (this.CONFIG.DirectionRecoater[0]) {
          sprdtext.rotation.set(0, 0, 0)
          sprdtext.position.set((length / 2) - 8, (width / 2) + 22, 0)
        } else {
          sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
          sprdtext.position.set((length / 2) + 22, (width / 2) - 10, 0)
        }
        this.scene.add(sprdtext)
        // sprdtext = new Text2D('flow', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        // sprdtext.scale.set(0.1, 0.1, 0.1)
        // if (this.CONFIG.DirectionRecoater[0]) {
        //   sprdtext.rotation.set(0, 0, -0.5 * Math.PI)
        //   sprdtext.position.set((length / 2) + 22, (width / 2) - 10, 0)
        // } else {
        //   sprdtext.rotation.set(0, 0, 0)
        //   sprdtext.position.set((length / 2) - 8, (width / 2) + 22, 0)
        // }
        // this.scene.add(sprdtext)
        sprdtext = new Text2D('x', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-(length / 2) - 15 + 40, -(width / 2) - 10, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('y', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(-(length / 2) - 15, -(width / 2) - 10 + 40, 0)
        this.scene.add(sprdtext)
        sprdtext = new Text2D('z', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.1, 0.1, 0.1)
        sprdtext.rotation.set(0.5 * Math.PI, 0, 0)
        sprdtext.position.set(-(length / 2) - 15, -(width / 2) - 10, 40)
        this.scene.add(sprdtext)
        sprdtext = new Text2D(length + ' x ' + width + ' x ' + height + 'mm', { align: textAlign.center, font: '90px Arial', fillStyle: '#000000', antialias: true })
        sprdtext.scale.set(0.15, 0.15, 0.15)
        sprdtext.rotation.set(0, 0, 0)
        sprdtext.position.set(0, -(width / 2) - 24, 0)
        this.scene.add(sprdtext)

        this.groupBase.add(buildPlate)
        this.groupBase.position.z = -(thickness / 2)
        this.scene.add(this.groupBase)
        break
    }
  }

  setupInset () {
    var insetWidth = 150
    var insetHeight = 150
    // insetWidth = window.innerWidth
    // insetHeight = window.innerHeight
    this.container = document.getElementById('inset')
    this.container.width = insetWidth
    this.container.height = insetHeight

    // renderer
    this.renderer2 = new this.THREE.WebGLRenderer({alpha: true})
    this.renderer2.setClearColor(0x000000, 0)
    this.renderer2.setSize(insetWidth, insetHeight)
    this.container.appendChild(this.renderer2.domElement)

    // scene
    this.scene2 = new this.THREE.Scene()

    // camera
    this.camera2 = new this.THREE.PerspectiveCamera(50, insetWidth / insetHeight, 1, 1000)
    this.camera2.up = this.camera.up // important!

    // axis
    var axis2 = new this.THREE.AxisHelper(100)
    this.scene2.add(axis2)
  }

  addLegend (min, max) {
    while (this['sceneL'].children.length > 0) {
      this['sceneL'].remove(this['sceneL'].children[0])
    }
    // add legend
    this.lut = new this.THREE.Lut(this.lutConfig.colorMap, this.lutConfig.numberOfColors)
    var lut = this.lut.copy(this.lut)
    this.lut.setMax(max)
    this.lut.setMin(min)
    var legend = lut.setLegendOn({ 'layout': 'horizontal', 'position': { 'x': 0, 'y': 0, 'z': 0 } })
    this['sceneL'].remove(legend)
    this['sceneL'].add(legend)
    var labels = lut.setLegendLabels({ 'ticks': 5, 'decimal': 4 })
    for (var i = 0; i < Object.keys(labels['ticks']).length; i++) {
      this['sceneL'].add(labels[ 'ticks' ][ i ])
      this['sceneL'].add(labels[ 'lines' ][ i ])
    }
    this.setLegendToElem(min, max)
  }

  setupInsetLegend () {
    var insetWidth = 300
    var insetHeight = 150

    // SCENE
    this['sceneL'] = new this.THREE.Scene()

    // CAMERA
    this['cameraL'] = new this.THREE.PerspectiveCamera(20, insetWidth / insetHeight, 1, 10000)
    this['cameraL'].name = 'camera'
    this['sceneL'].add(this['cameraL'])
    this['cameraL'].position.x = 0
    this['cameraL'].position.y = 0
    this['cameraL'].position.z = 5
    this['containerL'] = document.getElementById('inset-legend-wrp')
    this['containerL'].width = insetWidth
    this['containerL'].height = insetHeight

    // renderer
    this['rendererL'] = new this.THREE.WebGLRenderer({alpha: true})
    this['rendererL'].setClearColor(0x000000, 0)
    this['rendererL'].setSize(insetWidth, insetHeight)
    this['containerL'].appendChild(this['rendererL'].domElement)
  }

  animate () {
    if (this.modifySupportsMode && this.finishedLoading) {
      this.colorFaces()
    }

    if (this.camera.getWorldDirection().angleTo(new this.THREE.Vector3(0, 0, -1)) > Math.PI / 2) {
      this.groupBase.visible = false
    } else {
      this.groupBase.visible = true
    }

    this.cancelID = requestAnimationFrame(this.animate.bind(this))
    this.controls.update()
    // copy position of the camera into inset
    this.camera2.position.copy(this.camera.position)
    this.camera2.position.sub(this.controls.target)
    this.camera2.position.setLength(300)
    this.camera2.lookAt(this.scene2.position)
    this.renderer.render(this.scene, this.camera)
    this.renderer2.render(this.scene2, this.camera2)
    if (!this.modifySupportsMode && this.finishedLoading && this.isThermalPredict) {
      this['rendererL'].render(this['sceneL'], this['cameraL'])
    }
  }

  destroy () {
    cancelAnimationFrame(this.cancelID)
    this.renderer.domElement.addEventListener('dblclick', null, false)
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('click', this.onDocumentMouseClick)
    window.removeEventListener('mousemove', this.onDocumentMouseMove)
    this.scene = null
    this.projector = null
    this.camera = null
    this.controls = null
    function empty (elem) {
      while (elem.lastChild) elem.removeChild(elem.lastChild)
      elem.remove()
    }
    empty(this.renderer.domElement)
    empty(this.renderer2.domElement)
    // empty(this.gui.domElement)
    if (!this.modifySupportsMode && this.isThermalPredict) {
      empty(this['rendererL'].domElement)
    }
    this.renderer.dispose()
    this.renderer2.dispose()
    if (!this.modifySupportsMode && this.isThermalPredict) {
      this['rendererL'].dispose()
    }
    document.body.style.margin = '0'
    document.body.style.overflow = 'auto'
  }
}



// WEBPACK FOOTER //
// ./src/services/viewer.js